/* eslint-disable */
import _m0 from "protobufjs/minimal";
import { Timestamp } from "../../../google/protobuf/timestamp";

export const protobufPackage = "auth";

/** LoginBeginRequest initiates SSH-based authentication flow */
export interface LoginBeginRequest {
  /** OpenSSH format public key */
  sshPublicKey: string;
  /** user@host from CLI */
  origin: string;
  /** Optional VPN IP (172.24/172.25) */
  vpnIp: string;
  /** If true, extend existing token instead of creating new */
  extendExistingAuth: boolean;
  /** Optional: requested token duration (e.g., "2m", "15m", "1h") */
  requestedTokenDuration: string;
}

/** LoginBeginResponse contains challenge for SSH signature */
export interface LoginBeginResponse {
  /** UUID for this login session */
  sessionId: string;
  /** 32 random bytes to sign */
  nonce: Uint8Array;
  /** True if extend_existing_auth was requested but token was cleared/missing */
  noTokenToExtend: boolean;
  /** Human-readable reason why token cannot be extended (e.g., "token was cleared", "never authenticated") */
  noTokenReason: string;
}

/** LoginCompleteRequest submits SSH signature proof */
export interface LoginCompleteRequest {
  /** Session ID from BeginLogin */
  sessionId: string;
  /** Same public key as BeginLogin */
  sshPublicKey: string;
  /** SSH signature over nonce */
  signature: Uint8Array;
}

/** LoginCompleteResponse returns auth token on successful verification */
export interface LoginCompleteResponse {
  /** Signed token for subsequent requests */
  authToken: string;
  /** When the token expires */
  expiresAt: Date | undefined;
}

/** HasRoleRequest checks if user has one or more roles */
export interface HasRoleRequest {
  /** Mailbox address of requestor */
  mailboxAddress: string;
  /** DEPRECATED - single role as JSON (for v1 compatibility) */
  role: Uint8Array;
  /** List of roles to check (endpoint paths or database roles) */
  roles: string[];
}

/** HasRoleResponse indicates whether user has any of the requested roles */
export interface HasRoleResponse {
  /** Whether user has at least one of the roles */
  allowed: boolean;
  /** Optional explanation */
  reason: string;
  /** Which roles the user has (from the requested list) */
  rolesMatched: string[];
}

/** GetUserInfoRequest looks up user details by auth token */
export interface GetUserInfoRequest {
  /** Auth token from login */
  authToken: string;
}

/** UserGroupInfo contains information about a group membership */
export interface UserGroupInfo {
  /** Group UID */
  uid: string;
  /** Group name */
  name: string;
  /** Group description */
  description: string;
}

/** GetUserInfoResponse returns user information */
export interface GetUserInfoResponse {
  /** User UID */
  userUid: string;
  /** User name */
  name: string;
  /** User email (may be empty) */
  email: string;
  /** User description */
  description: string;
  /** Direct group memberships */
  groups: UserGroupInfo[];
  /** External maintainer (may be empty) */
  externalMaintainer: string;
  /** Extra config as JSON */
  extraConfig: Uint8Array;
  /** When the auth token expires */
  expiresAt:
    | Date
    | undefined;
  /** Extension duration (e.g., "1 hour") */
  expiresExtension: string;
}

/**
 * GetUserInfoByMailboxRequest looks up user details by mailbox address
 * Requires: mailbox.info role (auto-granted for now)
 */
export interface GetUserInfoByMailboxRequest {
  /** Mailbox address to look up */
  mailboxAddress: string;
}

/** GetUserInfoByMailboxResponse returns user information */
export interface GetUserInfoByMailboxResponse {
  /** User UID */
  userUid: string;
  /** User name */
  name: string;
  /** User email (may be empty) */
  email: string;
  /** User description */
  description: string;
  /** Direct group memberships */
  groups: UserGroupInfo[];
  /** External maintainer (may be empty) */
  externalMaintainer: string;
  /** Extra config as JSON */
  extraConfig: Uint8Array;
}

/**
 * GetUserInfoForSelfRequest requests authenticated user's own information
 * No parameters needed - uses caller's identity from RpcContext
 */
export interface GetUserInfoForSelfRequest {
}

/**
 * GetUserInfoForSelfResponse returns authenticated user's information.
 * The worker_* and server_* fields are populated only when the caller's
 * usergroup is bound to a worker row (worker rows); they
 * are empty for plain user logins.
 */
export interface GetUserInfoForSelfResponse {
  /** User UID */
  userUid: string;
  /** User name */
  name: string;
  /** User email (may be empty) */
  email: string;
  /** User description */
  description: string;
  /** Direct group memberships */
  groups: UserGroupInfo[];
  /** External maintainer (may be empty) */
  externalMaintainer: string;
  /** Extra config as JSON */
  extraConfig: Uint8Array;
  /** When the auth token expires */
  expiresAt:
    | Date
    | undefined;
  /** Extension duration (e.g., "1 hour") */
  expiresExtension: string;
  /** worker.uid (empty for plain user logins) */
  workerUid: string;
  /** "<login>@<server.name>" (empty for plain user logins) */
  workerLabel: string;
  /** server.uid bound to the worker (empty for plain user logins) */
  serverUid: string;
  /** server.name (empty for plain user logins) */
  serverLabel: string;
}

/** ManageRoleOverrideRequest grants or denies a role for a user */
export interface ManageRoleOverrideRequest {
  /** Email address or user UID */
  userIdentifier: string;
  /** Role name (e.g., "ContinuumUser") */
  role: string;
  /** true = grant, false = deny */
  granted: boolean;
  /** Reason for override */
  reason: string;
  /** Optional: Go duration string (e.g., "24h", "168h") */
  duration: string;
}

/** ManageRoleOverrideResponse confirms role override was created/updated */
export interface ManageRoleOverrideResponse {
  /** Whether operation succeeded */
  success: boolean;
  /** Human-readable message */
  message: string;
  /** UID of the user affected */
  userUid: string;
  /** Display label (email or name) */
  userLabel: string;
  /** ISO 8601 timestamp if duration was set */
  expiresAt: string;
}

/**
 * ImpersonateUserRequest asks (as a ContinuumAdmin) for a session token that
 * authenticates AS another user, so an admin can view the app from that user's
 * perspective. The minted auth row links back (delegateauthuid) to the admin's
 * own auth row it was created from — the record shows "X impersonating Y".
 */
export interface ImpersonateUserRequest {
  /** usergroup.uid of the user to impersonate */
  targetUserUid: string;
  /** optional audit reason */
  reason: string;
}

/** ImpersonateUserResponse returns a session token bound to the target user. */
export interface ImpersonateUserResponse {
  /** token authenticating AS the target user */
  sessionToken: string;
  /** the impersonated user's uid (Y) */
  targetUserUid: string;
  /** the impersonated user's name */
  targetName: string;
  /** the impersonated user's email (may be empty) */
  targetEmail: string;
  /** the impersonated user's groups */
  targetGroups: UserGroupInfo[];
  /** the admin who minted this (X) */
  impersonatorUid: string;
  /** the admin's name (for the "viewing as" banner) */
  impersonatorName: string;
  /** ISO 8601 token expiry */
  expiresAt: string;
}

/** RemoveRoleOverrideRequest deletes a role override */
export interface RemoveRoleOverrideRequest {
  /** Email address or user UID */
  userIdentifier: string;
  /** Role name to remove */
  role: string;
}

/** RemoveRoleOverrideResponse confirms removal */
export interface RemoveRoleOverrideResponse {
  /** Whether operation succeeded */
  success: boolean;
  /** Human-readable message */
  message: string;
  /** UID of the user affected */
  userUid: string;
}

/** EndpointAuthInfo contains information about a single endpoint_auth record */
export interface EndpointAuthInfo {
  uid: string;
  /** Group UID (null for defaults) */
  usergroupUid: string;
  /** Group name */
  usergroupName: string;
  /** Group email */
  usergroupEmail: string;
  endpointPattern: string;
  granted: boolean;
  priority: number;
  reason: string;
  createdAt: string;
  createdBy: string;
  expiresAt: string;
  /** Last update timestamp */
  updatedAt: string;
  /** Who last updated this grant */
  updatedBy: string;
  /** Policy name (if managed by a policy) */
  externalMaintainer: string;
  /** Policy UID (if managed by a policy) */
  policyUid: string;
  /** Additional metadata as JSON */
  extradata: Uint8Array;
}

/** TestEndpointAccessRequest tests authorization for a user+endpoint */
export interface TestEndpointAccessRequest {
  /** Email address or user UID */
  userIdentifier: string;
  /** Endpoint pattern to test (e.g., "auth.v2.GetUserInfo") */
  endpointPattern: string;
  /** Include all evaluated policies (not just matched ones) */
  includeAllPolicies: boolean;
}

/** PolicyMatchInfo contains details about a policy evaluation */
export interface PolicyMatchInfo {
  /** The policy record */
  policy:
    | EndpointAuthInfo
    | undefined;
  /** Whether this policy matched the endpoint */
  matched: boolean;
  /** Pattern match specificity score */
  specificity: number;
  /** Whether this was the winning policy */
  selected: boolean;
}

/** TestEndpointAccessResponse returns authorization test results */
export interface TestEndpointAccessResponse {
  /** Whether access is allowed */
  allowed: boolean;
  /** Explanation of the decision */
  reason: string;
  /** Resolved user UID */
  userUid: string;
  /** User email */
  userEmail: string;
  /** User display name */
  userName: string;
  /** User's group names (for display) */
  userGroupNames: string[];
  /** User's group UIDs (for filtering) */
  userGroupUids: string[];
  /** The policy that made the final decision */
  winningPolicy:
    | PolicyMatchInfo
    | undefined;
  /** All policies that matched */
  matchedPolicies: PolicyMatchInfo[];
  /** All evaluated policies (if requested) */
  evaluatedPolicies: PolicyMatchInfo[];
  /** True if no policy matched (fallback to deny) */
  noPolicyMatched: boolean;
}

/** SyncPoliciesRequest contains policy and group definitions to sync */
export interface SyncPoliciesRequest {
  /** Preview changes without applying */
  dryRun: boolean;
  /** Check for drift only */
  verifyOnly: boolean;
  /** JSON-serialized policy definitions */
  policiesJson: string;
  /** JSON-serialized group definitions */
  groupsJson: string;
}

/** SyncPoliciesResponse returns results of policy sync operation */
export interface SyncPoliciesResponse {
  success: boolean;
  message: string;
  groupsAdded: number;
  groupsUpdated: number;
  policiesAdded: number;
  policiesUpdated: number;
  policiesDeleted: number;
  /** Error messages if any */
  errors: string[];
}

/** PolicyDefinition represents a single authorization policy */
export interface PolicyDefinition {
  /** Empty for default policies, else group name (natural key - resolved to UID at sync time) */
  usergroupName: string;
  /** RPC endpoint pattern (e.g., "auth.v2.LoginBegin", "runner.v2.*") */
  endpointPattern: string;
  /** true = allow, false = deny */
  granted: boolean;
  /** Higher priority wins conflicts */
  priority: number;
  /** Human-readable explanation */
  reason: string;
  /** Optional metadata (use string values for JSON compatibility) */
  extraData: { [key: string]: string };
}

export interface PolicyDefinition_ExtraDataEntry {
  key: string;
  value: string;
}

/** GroupDefinition represents a user group */
export interface GroupDefinition {
  /** Human-readable name (e.g., "ContinuumUser") - natural key, UID resolved at sync time */
  name: string;
  /** Group purpose/description */
  description: string;
  /**
   * Groups that are MEMBERS of this one, by name. Membership NESTS: is_authorized_to()
   * resolves through memberships_for_user(uid, true), so a user holding a member group is
   * authorized to this group too. Direction matters and is easy to invert — listing
   * "ContinuumAdmin" here means every admin ALSO holds this group, not the reverse.
   * Declared rather than granted by hand so a fresh environment comes up with the nesting
   * instead of silently lacking it.
   * BUG-20260822-cdc-firehose-gated-on-the-coarse-user-role
   */
  members: string[];
}

/** TableAccessRule represents a table-level access control rule */
export interface TableAccessRule {
  /** Group name (resolved to UID at sync time), empty = default */
  usergroupName: string;
  /** Virtual table name from table_metadata */
  tableName: string;
  /** e.g., ["select-sql","fetch-data","insert-data"] */
  accessModes: string[];
  /** true = allow, false = deny */
  granted: boolean;
  /** Higher priority wins conflicts */
  priority: number;
  /** Human-readable explanation */
  reason: string;
  /** Optional SQL expr AND'd into SELECT/UPDATE; supports queryvar.useruid placeholder */
  rowFilter: string;
  /** When this matching GRANT is reached (highest priority first), stop composing lower-priority grant filters (soft-delete still composes) */
  terminal: boolean;
}

/** Policy represents a complete policy with groups and grants */
export interface Policy {
  /** Policy name (e.g., "godev", "acme-corp") */
  name: string;
  /** Semantic version or generation ID */
  version: string;
  /** Policy description */
  description: string;
  /** Groups to create/update */
  groups: GroupDefinition[];
  /** Policies to sync */
  policies: PolicyDefinition[];
  /** Additional metadata */
  metadata: { [key: string]: string };
  /** Table-level access rules to sync */
  tableAccess: TableAccessRule[];
}

export interface Policy_MetadataEntry {
  key: string;
  value: string;
}

/** PolicyInfo is the database-stored representation */
export interface PolicyInfo {
  /** Primary key */
  uid: string;
  /** Policy name (used as external_maintainer) */
  name: string;
  /** The actual policy */
  policy:
    | Policy
    | undefined;
  /** ISO-8601 timestamp */
  createdAt: string;
  /** Creator identifier */
  createdBy: string;
  /** ISO-8601 timestamp */
  updatedAt: string;
  /** Updater identifier */
  updatedBy: string;
  /** Whether this policy is active */
  active: boolean;
}

/** UpsertPolicyRequest stores a policy definition */
export interface UpsertPolicyRequest {
  /** The policy to store */
  policy:
    | Policy
    | undefined;
  /** Who is creating/updating this */
  createdBy: string;
}

/** UpsertPolicyResponse confirms storage */
export interface UpsertPolicyResponse {
  success: boolean;
  message: string;
  /** UID of stored policy */
  policyUid: string;
}

/** ListPoliciesRequest lists stored policies */
export interface ListPoliciesRequest {
  /** Filter to only active policies */
  activeOnly: boolean;
}

/** ListPoliciesResponse returns stored policies */
export interface ListPoliciesResponse {
  policies: PolicyInfo[];
}

/** ApplyPolicyRequest applies a stored policy */
export interface ApplyPolicyRequest {
  /** Name of policy to apply */
  policyName: string;
  /** Preview without applying */
  dryRun: boolean;
  /**
   * Policy to evaluate, as JSON. When set, the server uses this instead of
   * reading the stored row -- so a dry run never has to stage the policy in
   * the database first. Empty = read the stored policy named policy_name.
   */
  policyJson: string;
}

/** ApplyPolicyResponse returns results of applying a policy */
export interface ApplyPolicyResponse {
  success: boolean;
  message: string;
  groupsAdded: number;
  groupsUpdated: number;
  policiesAdded: number;
  policiesUpdated: number;
  policiesDeleted: number;
  errors: string[];
  /** table_access counts: computed all along, never reported. */
  tableAccessAdded: number;
  tableAccessUpdated: number;
  tableAccessDeleted: number;
  /**
   * Human-readable drift lines ("Would add group: X"). The server computed these
   * and logged them locally, so a dry run's whole report died in the auth
   * service's log and the operator saw an empty, exit-0 run.
   */
  drift: string[];
}

function createBaseLoginBeginRequest(): LoginBeginRequest {
  return { sshPublicKey: "", origin: "", vpnIp: "", extendExistingAuth: false, requestedTokenDuration: "" };
}

export const LoginBeginRequest = {
  encode(message: LoginBeginRequest, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.sshPublicKey !== "") {
      writer.uint32(10).string(message.sshPublicKey);
    }
    if (message.origin !== "") {
      writer.uint32(18).string(message.origin);
    }
    if (message.vpnIp !== "") {
      writer.uint32(26).string(message.vpnIp);
    }
    if (message.extendExistingAuth === true) {
      writer.uint32(32).bool(message.extendExistingAuth);
    }
    if (message.requestedTokenDuration !== "") {
      writer.uint32(42).string(message.requestedTokenDuration);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): LoginBeginRequest {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseLoginBeginRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.sshPublicKey = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.origin = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.vpnIp = reader.string();
          continue;
        case 4:
          if (tag !== 32) {
            break;
          }

          message.extendExistingAuth = reader.bool();
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.requestedTokenDuration = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): LoginBeginRequest {
    return {
      sshPublicKey: isSet(object.sshPublicKey) ? globalThis.String(object.sshPublicKey) : "",
      origin: isSet(object.origin) ? globalThis.String(object.origin) : "",
      vpnIp: isSet(object.vpnIp) ? globalThis.String(object.vpnIp) : "",
      extendExistingAuth: isSet(object.extendExistingAuth) ? globalThis.Boolean(object.extendExistingAuth) : false,
      requestedTokenDuration: isSet(object.requestedTokenDuration)
        ? globalThis.String(object.requestedTokenDuration)
        : "",
    };
  },

  toJSON(message: LoginBeginRequest): unknown {
    const obj: any = {};
    if (message.sshPublicKey !== "") {
      obj.sshPublicKey = message.sshPublicKey;
    }
    if (message.origin !== "") {
      obj.origin = message.origin;
    }
    if (message.vpnIp !== "") {
      obj.vpnIp = message.vpnIp;
    }
    if (message.extendExistingAuth === true) {
      obj.extendExistingAuth = message.extendExistingAuth;
    }
    if (message.requestedTokenDuration !== "") {
      obj.requestedTokenDuration = message.requestedTokenDuration;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<LoginBeginRequest>, I>>(base?: I): LoginBeginRequest {
    return LoginBeginRequest.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<LoginBeginRequest>, I>>(object: I): LoginBeginRequest {
    const message = createBaseLoginBeginRequest();
    message.sshPublicKey = object.sshPublicKey ?? "";
    message.origin = object.origin ?? "";
    message.vpnIp = object.vpnIp ?? "";
    message.extendExistingAuth = object.extendExistingAuth ?? false;
    message.requestedTokenDuration = object.requestedTokenDuration ?? "";
    return message;
  },
};

function createBaseLoginBeginResponse(): LoginBeginResponse {
  return { sessionId: "", nonce: new Uint8Array(0), noTokenToExtend: false, noTokenReason: "" };
}

export const LoginBeginResponse = {
  encode(message: LoginBeginResponse, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.sessionId !== "") {
      writer.uint32(10).string(message.sessionId);
    }
    if (message.nonce.length !== 0) {
      writer.uint32(18).bytes(message.nonce);
    }
    if (message.noTokenToExtend === true) {
      writer.uint32(24).bool(message.noTokenToExtend);
    }
    if (message.noTokenReason !== "") {
      writer.uint32(34).string(message.noTokenReason);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): LoginBeginResponse {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseLoginBeginResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.sessionId = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.nonce = reader.bytes();
          continue;
        case 3:
          if (tag !== 24) {
            break;
          }

          message.noTokenToExtend = reader.bool();
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.noTokenReason = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): LoginBeginResponse {
    return {
      sessionId: isSet(object.sessionId) ? globalThis.String(object.sessionId) : "",
      nonce: isSet(object.nonce) ? bytesFromBase64(object.nonce) : new Uint8Array(0),
      noTokenToExtend: isSet(object.noTokenToExtend) ? globalThis.Boolean(object.noTokenToExtend) : false,
      noTokenReason: isSet(object.noTokenReason) ? globalThis.String(object.noTokenReason) : "",
    };
  },

  toJSON(message: LoginBeginResponse): unknown {
    const obj: any = {};
    if (message.sessionId !== "") {
      obj.sessionId = message.sessionId;
    }
    if (message.nonce.length !== 0) {
      obj.nonce = base64FromBytes(message.nonce);
    }
    if (message.noTokenToExtend === true) {
      obj.noTokenToExtend = message.noTokenToExtend;
    }
    if (message.noTokenReason !== "") {
      obj.noTokenReason = message.noTokenReason;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<LoginBeginResponse>, I>>(base?: I): LoginBeginResponse {
    return LoginBeginResponse.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<LoginBeginResponse>, I>>(object: I): LoginBeginResponse {
    const message = createBaseLoginBeginResponse();
    message.sessionId = object.sessionId ?? "";
    message.nonce = object.nonce ?? new Uint8Array(0);
    message.noTokenToExtend = object.noTokenToExtend ?? false;
    message.noTokenReason = object.noTokenReason ?? "";
    return message;
  },
};

function createBaseLoginCompleteRequest(): LoginCompleteRequest {
  return { sessionId: "", sshPublicKey: "", signature: new Uint8Array(0) };
}

export const LoginCompleteRequest = {
  encode(message: LoginCompleteRequest, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.sessionId !== "") {
      writer.uint32(10).string(message.sessionId);
    }
    if (message.sshPublicKey !== "") {
      writer.uint32(18).string(message.sshPublicKey);
    }
    if (message.signature.length !== 0) {
      writer.uint32(26).bytes(message.signature);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): LoginCompleteRequest {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseLoginCompleteRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.sessionId = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.sshPublicKey = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.signature = reader.bytes();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): LoginCompleteRequest {
    return {
      sessionId: isSet(object.sessionId) ? globalThis.String(object.sessionId) : "",
      sshPublicKey: isSet(object.sshPublicKey) ? globalThis.String(object.sshPublicKey) : "",
      signature: isSet(object.signature) ? bytesFromBase64(object.signature) : new Uint8Array(0),
    };
  },

  toJSON(message: LoginCompleteRequest): unknown {
    const obj: any = {};
    if (message.sessionId !== "") {
      obj.sessionId = message.sessionId;
    }
    if (message.sshPublicKey !== "") {
      obj.sshPublicKey = message.sshPublicKey;
    }
    if (message.signature.length !== 0) {
      obj.signature = base64FromBytes(message.signature);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<LoginCompleteRequest>, I>>(base?: I): LoginCompleteRequest {
    return LoginCompleteRequest.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<LoginCompleteRequest>, I>>(object: I): LoginCompleteRequest {
    const message = createBaseLoginCompleteRequest();
    message.sessionId = object.sessionId ?? "";
    message.sshPublicKey = object.sshPublicKey ?? "";
    message.signature = object.signature ?? new Uint8Array(0);
    return message;
  },
};

function createBaseLoginCompleteResponse(): LoginCompleteResponse {
  return { authToken: "", expiresAt: undefined };
}

export const LoginCompleteResponse = {
  encode(message: LoginCompleteResponse, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.authToken !== "") {
      writer.uint32(10).string(message.authToken);
    }
    if (message.expiresAt !== undefined) {
      Timestamp.encode(toTimestamp(message.expiresAt), writer.uint32(18).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): LoginCompleteResponse {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseLoginCompleteResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.authToken = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.expiresAt = fromTimestamp(Timestamp.decode(reader, reader.uint32()));
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): LoginCompleteResponse {
    return {
      authToken: isSet(object.authToken) ? globalThis.String(object.authToken) : "",
      expiresAt: isSet(object.expiresAt) ? fromJsonTimestamp(object.expiresAt) : undefined,
    };
  },

  toJSON(message: LoginCompleteResponse): unknown {
    const obj: any = {};
    if (message.authToken !== "") {
      obj.authToken = message.authToken;
    }
    if (message.expiresAt !== undefined) {
      obj.expiresAt = message.expiresAt.toISOString();
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<LoginCompleteResponse>, I>>(base?: I): LoginCompleteResponse {
    return LoginCompleteResponse.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<LoginCompleteResponse>, I>>(object: I): LoginCompleteResponse {
    const message = createBaseLoginCompleteResponse();
    message.authToken = object.authToken ?? "";
    message.expiresAt = object.expiresAt ?? undefined;
    return message;
  },
};

function createBaseHasRoleRequest(): HasRoleRequest {
  return { mailboxAddress: "", role: new Uint8Array(0), roles: [] };
}

export const HasRoleRequest = {
  encode(message: HasRoleRequest, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.mailboxAddress !== "") {
      writer.uint32(10).string(message.mailboxAddress);
    }
    if (message.role.length !== 0) {
      writer.uint32(18).bytes(message.role);
    }
    for (const v of message.roles) {
      writer.uint32(26).string(v!);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): HasRoleRequest {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseHasRoleRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.mailboxAddress = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.role = reader.bytes();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.roles.push(reader.string());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): HasRoleRequest {
    return {
      mailboxAddress: isSet(object.mailboxAddress) ? globalThis.String(object.mailboxAddress) : "",
      role: isSet(object.role) ? bytesFromBase64(object.role) : new Uint8Array(0),
      roles: globalThis.Array.isArray(object?.roles) ? object.roles.map((e: any) => globalThis.String(e)) : [],
    };
  },

  toJSON(message: HasRoleRequest): unknown {
    const obj: any = {};
    if (message.mailboxAddress !== "") {
      obj.mailboxAddress = message.mailboxAddress;
    }
    if (message.role.length !== 0) {
      obj.role = base64FromBytes(message.role);
    }
    if (message.roles?.length) {
      obj.roles = message.roles;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<HasRoleRequest>, I>>(base?: I): HasRoleRequest {
    return HasRoleRequest.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<HasRoleRequest>, I>>(object: I): HasRoleRequest {
    const message = createBaseHasRoleRequest();
    message.mailboxAddress = object.mailboxAddress ?? "";
    message.role = object.role ?? new Uint8Array(0);
    message.roles = object.roles?.map((e) => e) || [];
    return message;
  },
};

function createBaseHasRoleResponse(): HasRoleResponse {
  return { allowed: false, reason: "", rolesMatched: [] };
}

export const HasRoleResponse = {
  encode(message: HasRoleResponse, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.allowed === true) {
      writer.uint32(8).bool(message.allowed);
    }
    if (message.reason !== "") {
      writer.uint32(18).string(message.reason);
    }
    for (const v of message.rolesMatched) {
      writer.uint32(26).string(v!);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): HasRoleResponse {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseHasRoleResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.allowed = reader.bool();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.reason = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.rolesMatched.push(reader.string());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): HasRoleResponse {
    return {
      allowed: isSet(object.allowed) ? globalThis.Boolean(object.allowed) : false,
      reason: isSet(object.reason) ? globalThis.String(object.reason) : "",
      rolesMatched: globalThis.Array.isArray(object?.rolesMatched)
        ? object.rolesMatched.map((e: any) => globalThis.String(e))
        : [],
    };
  },

  toJSON(message: HasRoleResponse): unknown {
    const obj: any = {};
    if (message.allowed === true) {
      obj.allowed = message.allowed;
    }
    if (message.reason !== "") {
      obj.reason = message.reason;
    }
    if (message.rolesMatched?.length) {
      obj.rolesMatched = message.rolesMatched;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<HasRoleResponse>, I>>(base?: I): HasRoleResponse {
    return HasRoleResponse.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<HasRoleResponse>, I>>(object: I): HasRoleResponse {
    const message = createBaseHasRoleResponse();
    message.allowed = object.allowed ?? false;
    message.reason = object.reason ?? "";
    message.rolesMatched = object.rolesMatched?.map((e) => e) || [];
    return message;
  },
};

function createBaseGetUserInfoRequest(): GetUserInfoRequest {
  return { authToken: "" };
}

export const GetUserInfoRequest = {
  encode(message: GetUserInfoRequest, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.authToken !== "") {
      writer.uint32(10).string(message.authToken);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): GetUserInfoRequest {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseGetUserInfoRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.authToken = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): GetUserInfoRequest {
    return { authToken: isSet(object.authToken) ? globalThis.String(object.authToken) : "" };
  },

  toJSON(message: GetUserInfoRequest): unknown {
    const obj: any = {};
    if (message.authToken !== "") {
      obj.authToken = message.authToken;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<GetUserInfoRequest>, I>>(base?: I): GetUserInfoRequest {
    return GetUserInfoRequest.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<GetUserInfoRequest>, I>>(object: I): GetUserInfoRequest {
    const message = createBaseGetUserInfoRequest();
    message.authToken = object.authToken ?? "";
    return message;
  },
};

function createBaseUserGroupInfo(): UserGroupInfo {
  return { uid: "", name: "", description: "" };
}

export const UserGroupInfo = {
  encode(message: UserGroupInfo, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.uid !== "") {
      writer.uint32(10).string(message.uid);
    }
    if (message.name !== "") {
      writer.uint32(18).string(message.name);
    }
    if (message.description !== "") {
      writer.uint32(26).string(message.description);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): UserGroupInfo {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseUserGroupInfo();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.uid = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.name = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.description = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): UserGroupInfo {
    return {
      uid: isSet(object.uid) ? globalThis.String(object.uid) : "",
      name: isSet(object.name) ? globalThis.String(object.name) : "",
      description: isSet(object.description) ? globalThis.String(object.description) : "",
    };
  },

  toJSON(message: UserGroupInfo): unknown {
    const obj: any = {};
    if (message.uid !== "") {
      obj.uid = message.uid;
    }
    if (message.name !== "") {
      obj.name = message.name;
    }
    if (message.description !== "") {
      obj.description = message.description;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<UserGroupInfo>, I>>(base?: I): UserGroupInfo {
    return UserGroupInfo.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<UserGroupInfo>, I>>(object: I): UserGroupInfo {
    const message = createBaseUserGroupInfo();
    message.uid = object.uid ?? "";
    message.name = object.name ?? "";
    message.description = object.description ?? "";
    return message;
  },
};

function createBaseGetUserInfoResponse(): GetUserInfoResponse {
  return {
    userUid: "",
    name: "",
    email: "",
    description: "",
    groups: [],
    externalMaintainer: "",
    extraConfig: new Uint8Array(0),
    expiresAt: undefined,
    expiresExtension: "",
  };
}

export const GetUserInfoResponse = {
  encode(message: GetUserInfoResponse, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.userUid !== "") {
      writer.uint32(10).string(message.userUid);
    }
    if (message.name !== "") {
      writer.uint32(18).string(message.name);
    }
    if (message.email !== "") {
      writer.uint32(26).string(message.email);
    }
    if (message.description !== "") {
      writer.uint32(34).string(message.description);
    }
    for (const v of message.groups) {
      UserGroupInfo.encode(v!, writer.uint32(42).fork()).ldelim();
    }
    if (message.externalMaintainer !== "") {
      writer.uint32(50).string(message.externalMaintainer);
    }
    if (message.extraConfig.length !== 0) {
      writer.uint32(58).bytes(message.extraConfig);
    }
    if (message.expiresAt !== undefined) {
      Timestamp.encode(toTimestamp(message.expiresAt), writer.uint32(66).fork()).ldelim();
    }
    if (message.expiresExtension !== "") {
      writer.uint32(74).string(message.expiresExtension);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): GetUserInfoResponse {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseGetUserInfoResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.userUid = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.name = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.email = reader.string();
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.description = reader.string();
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.groups.push(UserGroupInfo.decode(reader, reader.uint32()));
          continue;
        case 6:
          if (tag !== 50) {
            break;
          }

          message.externalMaintainer = reader.string();
          continue;
        case 7:
          if (tag !== 58) {
            break;
          }

          message.extraConfig = reader.bytes();
          continue;
        case 8:
          if (tag !== 66) {
            break;
          }

          message.expiresAt = fromTimestamp(Timestamp.decode(reader, reader.uint32()));
          continue;
        case 9:
          if (tag !== 74) {
            break;
          }

          message.expiresExtension = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): GetUserInfoResponse {
    return {
      userUid: isSet(object.userUid) ? globalThis.String(object.userUid) : "",
      name: isSet(object.name) ? globalThis.String(object.name) : "",
      email: isSet(object.email) ? globalThis.String(object.email) : "",
      description: isSet(object.description) ? globalThis.String(object.description) : "",
      groups: globalThis.Array.isArray(object?.groups) ? object.groups.map((e: any) => UserGroupInfo.fromJSON(e)) : [],
      externalMaintainer: isSet(object.externalMaintainer) ? globalThis.String(object.externalMaintainer) : "",
      extraConfig: isSet(object.extraConfig) ? bytesFromBase64(object.extraConfig) : new Uint8Array(0),
      expiresAt: isSet(object.expiresAt) ? fromJsonTimestamp(object.expiresAt) : undefined,
      expiresExtension: isSet(object.expiresExtension) ? globalThis.String(object.expiresExtension) : "",
    };
  },

  toJSON(message: GetUserInfoResponse): unknown {
    const obj: any = {};
    if (message.userUid !== "") {
      obj.userUid = message.userUid;
    }
    if (message.name !== "") {
      obj.name = message.name;
    }
    if (message.email !== "") {
      obj.email = message.email;
    }
    if (message.description !== "") {
      obj.description = message.description;
    }
    if (message.groups?.length) {
      obj.groups = message.groups.map((e) => UserGroupInfo.toJSON(e));
    }
    if (message.externalMaintainer !== "") {
      obj.externalMaintainer = message.externalMaintainer;
    }
    if (message.extraConfig.length !== 0) {
      obj.extraConfig = base64FromBytes(message.extraConfig);
    }
    if (message.expiresAt !== undefined) {
      obj.expiresAt = message.expiresAt.toISOString();
    }
    if (message.expiresExtension !== "") {
      obj.expiresExtension = message.expiresExtension;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<GetUserInfoResponse>, I>>(base?: I): GetUserInfoResponse {
    return GetUserInfoResponse.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<GetUserInfoResponse>, I>>(object: I): GetUserInfoResponse {
    const message = createBaseGetUserInfoResponse();
    message.userUid = object.userUid ?? "";
    message.name = object.name ?? "";
    message.email = object.email ?? "";
    message.description = object.description ?? "";
    message.groups = object.groups?.map((e) => UserGroupInfo.fromPartial(e)) || [];
    message.externalMaintainer = object.externalMaintainer ?? "";
    message.extraConfig = object.extraConfig ?? new Uint8Array(0);
    message.expiresAt = object.expiresAt ?? undefined;
    message.expiresExtension = object.expiresExtension ?? "";
    return message;
  },
};

function createBaseGetUserInfoByMailboxRequest(): GetUserInfoByMailboxRequest {
  return { mailboxAddress: "" };
}

export const GetUserInfoByMailboxRequest = {
  encode(message: GetUserInfoByMailboxRequest, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.mailboxAddress !== "") {
      writer.uint32(10).string(message.mailboxAddress);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): GetUserInfoByMailboxRequest {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseGetUserInfoByMailboxRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.mailboxAddress = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): GetUserInfoByMailboxRequest {
    return { mailboxAddress: isSet(object.mailboxAddress) ? globalThis.String(object.mailboxAddress) : "" };
  },

  toJSON(message: GetUserInfoByMailboxRequest): unknown {
    const obj: any = {};
    if (message.mailboxAddress !== "") {
      obj.mailboxAddress = message.mailboxAddress;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<GetUserInfoByMailboxRequest>, I>>(base?: I): GetUserInfoByMailboxRequest {
    return GetUserInfoByMailboxRequest.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<GetUserInfoByMailboxRequest>, I>>(object: I): GetUserInfoByMailboxRequest {
    const message = createBaseGetUserInfoByMailboxRequest();
    message.mailboxAddress = object.mailboxAddress ?? "";
    return message;
  },
};

function createBaseGetUserInfoByMailboxResponse(): GetUserInfoByMailboxResponse {
  return {
    userUid: "",
    name: "",
    email: "",
    description: "",
    groups: [],
    externalMaintainer: "",
    extraConfig: new Uint8Array(0),
  };
}

export const GetUserInfoByMailboxResponse = {
  encode(message: GetUserInfoByMailboxResponse, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.userUid !== "") {
      writer.uint32(10).string(message.userUid);
    }
    if (message.name !== "") {
      writer.uint32(18).string(message.name);
    }
    if (message.email !== "") {
      writer.uint32(26).string(message.email);
    }
    if (message.description !== "") {
      writer.uint32(34).string(message.description);
    }
    for (const v of message.groups) {
      UserGroupInfo.encode(v!, writer.uint32(42).fork()).ldelim();
    }
    if (message.externalMaintainer !== "") {
      writer.uint32(50).string(message.externalMaintainer);
    }
    if (message.extraConfig.length !== 0) {
      writer.uint32(58).bytes(message.extraConfig);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): GetUserInfoByMailboxResponse {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseGetUserInfoByMailboxResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.userUid = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.name = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.email = reader.string();
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.description = reader.string();
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.groups.push(UserGroupInfo.decode(reader, reader.uint32()));
          continue;
        case 6:
          if (tag !== 50) {
            break;
          }

          message.externalMaintainer = reader.string();
          continue;
        case 7:
          if (tag !== 58) {
            break;
          }

          message.extraConfig = reader.bytes();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): GetUserInfoByMailboxResponse {
    return {
      userUid: isSet(object.userUid) ? globalThis.String(object.userUid) : "",
      name: isSet(object.name) ? globalThis.String(object.name) : "",
      email: isSet(object.email) ? globalThis.String(object.email) : "",
      description: isSet(object.description) ? globalThis.String(object.description) : "",
      groups: globalThis.Array.isArray(object?.groups) ? object.groups.map((e: any) => UserGroupInfo.fromJSON(e)) : [],
      externalMaintainer: isSet(object.externalMaintainer) ? globalThis.String(object.externalMaintainer) : "",
      extraConfig: isSet(object.extraConfig) ? bytesFromBase64(object.extraConfig) : new Uint8Array(0),
    };
  },

  toJSON(message: GetUserInfoByMailboxResponse): unknown {
    const obj: any = {};
    if (message.userUid !== "") {
      obj.userUid = message.userUid;
    }
    if (message.name !== "") {
      obj.name = message.name;
    }
    if (message.email !== "") {
      obj.email = message.email;
    }
    if (message.description !== "") {
      obj.description = message.description;
    }
    if (message.groups?.length) {
      obj.groups = message.groups.map((e) => UserGroupInfo.toJSON(e));
    }
    if (message.externalMaintainer !== "") {
      obj.externalMaintainer = message.externalMaintainer;
    }
    if (message.extraConfig.length !== 0) {
      obj.extraConfig = base64FromBytes(message.extraConfig);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<GetUserInfoByMailboxResponse>, I>>(base?: I): GetUserInfoByMailboxResponse {
    return GetUserInfoByMailboxResponse.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<GetUserInfoByMailboxResponse>, I>>(object: I): GetUserInfoByMailboxResponse {
    const message = createBaseGetUserInfoByMailboxResponse();
    message.userUid = object.userUid ?? "";
    message.name = object.name ?? "";
    message.email = object.email ?? "";
    message.description = object.description ?? "";
    message.groups = object.groups?.map((e) => UserGroupInfo.fromPartial(e)) || [];
    message.externalMaintainer = object.externalMaintainer ?? "";
    message.extraConfig = object.extraConfig ?? new Uint8Array(0);
    return message;
  },
};

function createBaseGetUserInfoForSelfRequest(): GetUserInfoForSelfRequest {
  return {};
}

export const GetUserInfoForSelfRequest = {
  encode(_: GetUserInfoForSelfRequest, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): GetUserInfoForSelfRequest {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseGetUserInfoForSelfRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(_: any): GetUserInfoForSelfRequest {
    return {};
  },

  toJSON(_: GetUserInfoForSelfRequest): unknown {
    const obj: any = {};
    return obj;
  },

  create<I extends Exact<DeepPartial<GetUserInfoForSelfRequest>, I>>(base?: I): GetUserInfoForSelfRequest {
    return GetUserInfoForSelfRequest.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<GetUserInfoForSelfRequest>, I>>(_: I): GetUserInfoForSelfRequest {
    const message = createBaseGetUserInfoForSelfRequest();
    return message;
  },
};

function createBaseGetUserInfoForSelfResponse(): GetUserInfoForSelfResponse {
  return {
    userUid: "",
    name: "",
    email: "",
    description: "",
    groups: [],
    externalMaintainer: "",
    extraConfig: new Uint8Array(0),
    expiresAt: undefined,
    expiresExtension: "",
    workerUid: "",
    workerLabel: "",
    serverUid: "",
    serverLabel: "",
  };
}

export const GetUserInfoForSelfResponse = {
  encode(message: GetUserInfoForSelfResponse, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.userUid !== "") {
      writer.uint32(10).string(message.userUid);
    }
    if (message.name !== "") {
      writer.uint32(18).string(message.name);
    }
    if (message.email !== "") {
      writer.uint32(26).string(message.email);
    }
    if (message.description !== "") {
      writer.uint32(34).string(message.description);
    }
    for (const v of message.groups) {
      UserGroupInfo.encode(v!, writer.uint32(42).fork()).ldelim();
    }
    if (message.externalMaintainer !== "") {
      writer.uint32(50).string(message.externalMaintainer);
    }
    if (message.extraConfig.length !== 0) {
      writer.uint32(58).bytes(message.extraConfig);
    }
    if (message.expiresAt !== undefined) {
      Timestamp.encode(toTimestamp(message.expiresAt), writer.uint32(66).fork()).ldelim();
    }
    if (message.expiresExtension !== "") {
      writer.uint32(74).string(message.expiresExtension);
    }
    if (message.workerUid !== "") {
      writer.uint32(82).string(message.workerUid);
    }
    if (message.workerLabel !== "") {
      writer.uint32(90).string(message.workerLabel);
    }
    if (message.serverUid !== "") {
      writer.uint32(98).string(message.serverUid);
    }
    if (message.serverLabel !== "") {
      writer.uint32(106).string(message.serverLabel);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): GetUserInfoForSelfResponse {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseGetUserInfoForSelfResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.userUid = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.name = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.email = reader.string();
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.description = reader.string();
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.groups.push(UserGroupInfo.decode(reader, reader.uint32()));
          continue;
        case 6:
          if (tag !== 50) {
            break;
          }

          message.externalMaintainer = reader.string();
          continue;
        case 7:
          if (tag !== 58) {
            break;
          }

          message.extraConfig = reader.bytes();
          continue;
        case 8:
          if (tag !== 66) {
            break;
          }

          message.expiresAt = fromTimestamp(Timestamp.decode(reader, reader.uint32()));
          continue;
        case 9:
          if (tag !== 74) {
            break;
          }

          message.expiresExtension = reader.string();
          continue;
        case 10:
          if (tag !== 82) {
            break;
          }

          message.workerUid = reader.string();
          continue;
        case 11:
          if (tag !== 90) {
            break;
          }

          message.workerLabel = reader.string();
          continue;
        case 12:
          if (tag !== 98) {
            break;
          }

          message.serverUid = reader.string();
          continue;
        case 13:
          if (tag !== 106) {
            break;
          }

          message.serverLabel = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): GetUserInfoForSelfResponse {
    return {
      userUid: isSet(object.userUid) ? globalThis.String(object.userUid) : "",
      name: isSet(object.name) ? globalThis.String(object.name) : "",
      email: isSet(object.email) ? globalThis.String(object.email) : "",
      description: isSet(object.description) ? globalThis.String(object.description) : "",
      groups: globalThis.Array.isArray(object?.groups) ? object.groups.map((e: any) => UserGroupInfo.fromJSON(e)) : [],
      externalMaintainer: isSet(object.externalMaintainer) ? globalThis.String(object.externalMaintainer) : "",
      extraConfig: isSet(object.extraConfig) ? bytesFromBase64(object.extraConfig) : new Uint8Array(0),
      expiresAt: isSet(object.expiresAt) ? fromJsonTimestamp(object.expiresAt) : undefined,
      expiresExtension: isSet(object.expiresExtension) ? globalThis.String(object.expiresExtension) : "",
      workerUid: isSet(object.workerUid) ? globalThis.String(object.workerUid) : "",
      workerLabel: isSet(object.workerLabel) ? globalThis.String(object.workerLabel) : "",
      serverUid: isSet(object.serverUid) ? globalThis.String(object.serverUid) : "",
      serverLabel: isSet(object.serverLabel) ? globalThis.String(object.serverLabel) : "",
    };
  },

  toJSON(message: GetUserInfoForSelfResponse): unknown {
    const obj: any = {};
    if (message.userUid !== "") {
      obj.userUid = message.userUid;
    }
    if (message.name !== "") {
      obj.name = message.name;
    }
    if (message.email !== "") {
      obj.email = message.email;
    }
    if (message.description !== "") {
      obj.description = message.description;
    }
    if (message.groups?.length) {
      obj.groups = message.groups.map((e) => UserGroupInfo.toJSON(e));
    }
    if (message.externalMaintainer !== "") {
      obj.externalMaintainer = message.externalMaintainer;
    }
    if (message.extraConfig.length !== 0) {
      obj.extraConfig = base64FromBytes(message.extraConfig);
    }
    if (message.expiresAt !== undefined) {
      obj.expiresAt = message.expiresAt.toISOString();
    }
    if (message.expiresExtension !== "") {
      obj.expiresExtension = message.expiresExtension;
    }
    if (message.workerUid !== "") {
      obj.workerUid = message.workerUid;
    }
    if (message.workerLabel !== "") {
      obj.workerLabel = message.workerLabel;
    }
    if (message.serverUid !== "") {
      obj.serverUid = message.serverUid;
    }
    if (message.serverLabel !== "") {
      obj.serverLabel = message.serverLabel;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<GetUserInfoForSelfResponse>, I>>(base?: I): GetUserInfoForSelfResponse {
    return GetUserInfoForSelfResponse.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<GetUserInfoForSelfResponse>, I>>(object: I): GetUserInfoForSelfResponse {
    const message = createBaseGetUserInfoForSelfResponse();
    message.userUid = object.userUid ?? "";
    message.name = object.name ?? "";
    message.email = object.email ?? "";
    message.description = object.description ?? "";
    message.groups = object.groups?.map((e) => UserGroupInfo.fromPartial(e)) || [];
    message.externalMaintainer = object.externalMaintainer ?? "";
    message.extraConfig = object.extraConfig ?? new Uint8Array(0);
    message.expiresAt = object.expiresAt ?? undefined;
    message.expiresExtension = object.expiresExtension ?? "";
    message.workerUid = object.workerUid ?? "";
    message.workerLabel = object.workerLabel ?? "";
    message.serverUid = object.serverUid ?? "";
    message.serverLabel = object.serverLabel ?? "";
    return message;
  },
};

function createBaseManageRoleOverrideRequest(): ManageRoleOverrideRequest {
  return { userIdentifier: "", role: "", granted: false, reason: "", duration: "" };
}

export const ManageRoleOverrideRequest = {
  encode(message: ManageRoleOverrideRequest, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.userIdentifier !== "") {
      writer.uint32(10).string(message.userIdentifier);
    }
    if (message.role !== "") {
      writer.uint32(18).string(message.role);
    }
    if (message.granted === true) {
      writer.uint32(24).bool(message.granted);
    }
    if (message.reason !== "") {
      writer.uint32(34).string(message.reason);
    }
    if (message.duration !== "") {
      writer.uint32(42).string(message.duration);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ManageRoleOverrideRequest {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseManageRoleOverrideRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.userIdentifier = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.role = reader.string();
          continue;
        case 3:
          if (tag !== 24) {
            break;
          }

          message.granted = reader.bool();
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.reason = reader.string();
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.duration = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ManageRoleOverrideRequest {
    return {
      userIdentifier: isSet(object.userIdentifier) ? globalThis.String(object.userIdentifier) : "",
      role: isSet(object.role) ? globalThis.String(object.role) : "",
      granted: isSet(object.granted) ? globalThis.Boolean(object.granted) : false,
      reason: isSet(object.reason) ? globalThis.String(object.reason) : "",
      duration: isSet(object.duration) ? globalThis.String(object.duration) : "",
    };
  },

  toJSON(message: ManageRoleOverrideRequest): unknown {
    const obj: any = {};
    if (message.userIdentifier !== "") {
      obj.userIdentifier = message.userIdentifier;
    }
    if (message.role !== "") {
      obj.role = message.role;
    }
    if (message.granted === true) {
      obj.granted = message.granted;
    }
    if (message.reason !== "") {
      obj.reason = message.reason;
    }
    if (message.duration !== "") {
      obj.duration = message.duration;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ManageRoleOverrideRequest>, I>>(base?: I): ManageRoleOverrideRequest {
    return ManageRoleOverrideRequest.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ManageRoleOverrideRequest>, I>>(object: I): ManageRoleOverrideRequest {
    const message = createBaseManageRoleOverrideRequest();
    message.userIdentifier = object.userIdentifier ?? "";
    message.role = object.role ?? "";
    message.granted = object.granted ?? false;
    message.reason = object.reason ?? "";
    message.duration = object.duration ?? "";
    return message;
  },
};

function createBaseManageRoleOverrideResponse(): ManageRoleOverrideResponse {
  return { success: false, message: "", userUid: "", userLabel: "", expiresAt: "" };
}

export const ManageRoleOverrideResponse = {
  encode(message: ManageRoleOverrideResponse, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.success === true) {
      writer.uint32(8).bool(message.success);
    }
    if (message.message !== "") {
      writer.uint32(18).string(message.message);
    }
    if (message.userUid !== "") {
      writer.uint32(26).string(message.userUid);
    }
    if (message.userLabel !== "") {
      writer.uint32(34).string(message.userLabel);
    }
    if (message.expiresAt !== "") {
      writer.uint32(42).string(message.expiresAt);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ManageRoleOverrideResponse {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseManageRoleOverrideResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.success = reader.bool();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.message = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.userUid = reader.string();
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.userLabel = reader.string();
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.expiresAt = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ManageRoleOverrideResponse {
    return {
      success: isSet(object.success) ? globalThis.Boolean(object.success) : false,
      message: isSet(object.message) ? globalThis.String(object.message) : "",
      userUid: isSet(object.userUid) ? globalThis.String(object.userUid) : "",
      userLabel: isSet(object.userLabel) ? globalThis.String(object.userLabel) : "",
      expiresAt: isSet(object.expiresAt) ? globalThis.String(object.expiresAt) : "",
    };
  },

  toJSON(message: ManageRoleOverrideResponse): unknown {
    const obj: any = {};
    if (message.success === true) {
      obj.success = message.success;
    }
    if (message.message !== "") {
      obj.message = message.message;
    }
    if (message.userUid !== "") {
      obj.userUid = message.userUid;
    }
    if (message.userLabel !== "") {
      obj.userLabel = message.userLabel;
    }
    if (message.expiresAt !== "") {
      obj.expiresAt = message.expiresAt;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ManageRoleOverrideResponse>, I>>(base?: I): ManageRoleOverrideResponse {
    return ManageRoleOverrideResponse.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ManageRoleOverrideResponse>, I>>(object: I): ManageRoleOverrideResponse {
    const message = createBaseManageRoleOverrideResponse();
    message.success = object.success ?? false;
    message.message = object.message ?? "";
    message.userUid = object.userUid ?? "";
    message.userLabel = object.userLabel ?? "";
    message.expiresAt = object.expiresAt ?? "";
    return message;
  },
};

function createBaseImpersonateUserRequest(): ImpersonateUserRequest {
  return { targetUserUid: "", reason: "" };
}

export const ImpersonateUserRequest = {
  encode(message: ImpersonateUserRequest, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.targetUserUid !== "") {
      writer.uint32(10).string(message.targetUserUid);
    }
    if (message.reason !== "") {
      writer.uint32(18).string(message.reason);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ImpersonateUserRequest {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseImpersonateUserRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.targetUserUid = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.reason = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ImpersonateUserRequest {
    return {
      targetUserUid: isSet(object.targetUserUid) ? globalThis.String(object.targetUserUid) : "",
      reason: isSet(object.reason) ? globalThis.String(object.reason) : "",
    };
  },

  toJSON(message: ImpersonateUserRequest): unknown {
    const obj: any = {};
    if (message.targetUserUid !== "") {
      obj.targetUserUid = message.targetUserUid;
    }
    if (message.reason !== "") {
      obj.reason = message.reason;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ImpersonateUserRequest>, I>>(base?: I): ImpersonateUserRequest {
    return ImpersonateUserRequest.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ImpersonateUserRequest>, I>>(object: I): ImpersonateUserRequest {
    const message = createBaseImpersonateUserRequest();
    message.targetUserUid = object.targetUserUid ?? "";
    message.reason = object.reason ?? "";
    return message;
  },
};

function createBaseImpersonateUserResponse(): ImpersonateUserResponse {
  return {
    sessionToken: "",
    targetUserUid: "",
    targetName: "",
    targetEmail: "",
    targetGroups: [],
    impersonatorUid: "",
    impersonatorName: "",
    expiresAt: "",
  };
}

export const ImpersonateUserResponse = {
  encode(message: ImpersonateUserResponse, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.sessionToken !== "") {
      writer.uint32(10).string(message.sessionToken);
    }
    if (message.targetUserUid !== "") {
      writer.uint32(18).string(message.targetUserUid);
    }
    if (message.targetName !== "") {
      writer.uint32(26).string(message.targetName);
    }
    if (message.targetEmail !== "") {
      writer.uint32(34).string(message.targetEmail);
    }
    for (const v of message.targetGroups) {
      UserGroupInfo.encode(v!, writer.uint32(42).fork()).ldelim();
    }
    if (message.impersonatorUid !== "") {
      writer.uint32(50).string(message.impersonatorUid);
    }
    if (message.impersonatorName !== "") {
      writer.uint32(58).string(message.impersonatorName);
    }
    if (message.expiresAt !== "") {
      writer.uint32(66).string(message.expiresAt);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ImpersonateUserResponse {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseImpersonateUserResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.sessionToken = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.targetUserUid = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.targetName = reader.string();
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.targetEmail = reader.string();
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.targetGroups.push(UserGroupInfo.decode(reader, reader.uint32()));
          continue;
        case 6:
          if (tag !== 50) {
            break;
          }

          message.impersonatorUid = reader.string();
          continue;
        case 7:
          if (tag !== 58) {
            break;
          }

          message.impersonatorName = reader.string();
          continue;
        case 8:
          if (tag !== 66) {
            break;
          }

          message.expiresAt = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ImpersonateUserResponse {
    return {
      sessionToken: isSet(object.sessionToken) ? globalThis.String(object.sessionToken) : "",
      targetUserUid: isSet(object.targetUserUid) ? globalThis.String(object.targetUserUid) : "",
      targetName: isSet(object.targetName) ? globalThis.String(object.targetName) : "",
      targetEmail: isSet(object.targetEmail) ? globalThis.String(object.targetEmail) : "",
      targetGroups: globalThis.Array.isArray(object?.targetGroups)
        ? object.targetGroups.map((e: any) => UserGroupInfo.fromJSON(e))
        : [],
      impersonatorUid: isSet(object.impersonatorUid) ? globalThis.String(object.impersonatorUid) : "",
      impersonatorName: isSet(object.impersonatorName) ? globalThis.String(object.impersonatorName) : "",
      expiresAt: isSet(object.expiresAt) ? globalThis.String(object.expiresAt) : "",
    };
  },

  toJSON(message: ImpersonateUserResponse): unknown {
    const obj: any = {};
    if (message.sessionToken !== "") {
      obj.sessionToken = message.sessionToken;
    }
    if (message.targetUserUid !== "") {
      obj.targetUserUid = message.targetUserUid;
    }
    if (message.targetName !== "") {
      obj.targetName = message.targetName;
    }
    if (message.targetEmail !== "") {
      obj.targetEmail = message.targetEmail;
    }
    if (message.targetGroups?.length) {
      obj.targetGroups = message.targetGroups.map((e) => UserGroupInfo.toJSON(e));
    }
    if (message.impersonatorUid !== "") {
      obj.impersonatorUid = message.impersonatorUid;
    }
    if (message.impersonatorName !== "") {
      obj.impersonatorName = message.impersonatorName;
    }
    if (message.expiresAt !== "") {
      obj.expiresAt = message.expiresAt;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ImpersonateUserResponse>, I>>(base?: I): ImpersonateUserResponse {
    return ImpersonateUserResponse.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ImpersonateUserResponse>, I>>(object: I): ImpersonateUserResponse {
    const message = createBaseImpersonateUserResponse();
    message.sessionToken = object.sessionToken ?? "";
    message.targetUserUid = object.targetUserUid ?? "";
    message.targetName = object.targetName ?? "";
    message.targetEmail = object.targetEmail ?? "";
    message.targetGroups = object.targetGroups?.map((e) => UserGroupInfo.fromPartial(e)) || [];
    message.impersonatorUid = object.impersonatorUid ?? "";
    message.impersonatorName = object.impersonatorName ?? "";
    message.expiresAt = object.expiresAt ?? "";
    return message;
  },
};

function createBaseRemoveRoleOverrideRequest(): RemoveRoleOverrideRequest {
  return { userIdentifier: "", role: "" };
}

export const RemoveRoleOverrideRequest = {
  encode(message: RemoveRoleOverrideRequest, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.userIdentifier !== "") {
      writer.uint32(10).string(message.userIdentifier);
    }
    if (message.role !== "") {
      writer.uint32(18).string(message.role);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): RemoveRoleOverrideRequest {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseRemoveRoleOverrideRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.userIdentifier = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.role = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): RemoveRoleOverrideRequest {
    return {
      userIdentifier: isSet(object.userIdentifier) ? globalThis.String(object.userIdentifier) : "",
      role: isSet(object.role) ? globalThis.String(object.role) : "",
    };
  },

  toJSON(message: RemoveRoleOverrideRequest): unknown {
    const obj: any = {};
    if (message.userIdentifier !== "") {
      obj.userIdentifier = message.userIdentifier;
    }
    if (message.role !== "") {
      obj.role = message.role;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<RemoveRoleOverrideRequest>, I>>(base?: I): RemoveRoleOverrideRequest {
    return RemoveRoleOverrideRequest.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<RemoveRoleOverrideRequest>, I>>(object: I): RemoveRoleOverrideRequest {
    const message = createBaseRemoveRoleOverrideRequest();
    message.userIdentifier = object.userIdentifier ?? "";
    message.role = object.role ?? "";
    return message;
  },
};

function createBaseRemoveRoleOverrideResponse(): RemoveRoleOverrideResponse {
  return { success: false, message: "", userUid: "" };
}

export const RemoveRoleOverrideResponse = {
  encode(message: RemoveRoleOverrideResponse, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.success === true) {
      writer.uint32(8).bool(message.success);
    }
    if (message.message !== "") {
      writer.uint32(18).string(message.message);
    }
    if (message.userUid !== "") {
      writer.uint32(26).string(message.userUid);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): RemoveRoleOverrideResponse {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseRemoveRoleOverrideResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.success = reader.bool();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.message = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.userUid = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): RemoveRoleOverrideResponse {
    return {
      success: isSet(object.success) ? globalThis.Boolean(object.success) : false,
      message: isSet(object.message) ? globalThis.String(object.message) : "",
      userUid: isSet(object.userUid) ? globalThis.String(object.userUid) : "",
    };
  },

  toJSON(message: RemoveRoleOverrideResponse): unknown {
    const obj: any = {};
    if (message.success === true) {
      obj.success = message.success;
    }
    if (message.message !== "") {
      obj.message = message.message;
    }
    if (message.userUid !== "") {
      obj.userUid = message.userUid;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<RemoveRoleOverrideResponse>, I>>(base?: I): RemoveRoleOverrideResponse {
    return RemoveRoleOverrideResponse.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<RemoveRoleOverrideResponse>, I>>(object: I): RemoveRoleOverrideResponse {
    const message = createBaseRemoveRoleOverrideResponse();
    message.success = object.success ?? false;
    message.message = object.message ?? "";
    message.userUid = object.userUid ?? "";
    return message;
  },
};

function createBaseEndpointAuthInfo(): EndpointAuthInfo {
  return {
    uid: "",
    usergroupUid: "",
    usergroupName: "",
    usergroupEmail: "",
    endpointPattern: "",
    granted: false,
    priority: 0,
    reason: "",
    createdAt: "",
    createdBy: "",
    expiresAt: "",
    updatedAt: "",
    updatedBy: "",
    externalMaintainer: "",
    policyUid: "",
    extradata: new Uint8Array(0),
  };
}

export const EndpointAuthInfo = {
  encode(message: EndpointAuthInfo, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.uid !== "") {
      writer.uint32(10).string(message.uid);
    }
    if (message.usergroupUid !== "") {
      writer.uint32(18).string(message.usergroupUid);
    }
    if (message.usergroupName !== "") {
      writer.uint32(26).string(message.usergroupName);
    }
    if (message.usergroupEmail !== "") {
      writer.uint32(34).string(message.usergroupEmail);
    }
    if (message.endpointPattern !== "") {
      writer.uint32(42).string(message.endpointPattern);
    }
    if (message.granted === true) {
      writer.uint32(48).bool(message.granted);
    }
    if (message.priority !== 0) {
      writer.uint32(56).int32(message.priority);
    }
    if (message.reason !== "") {
      writer.uint32(66).string(message.reason);
    }
    if (message.createdAt !== "") {
      writer.uint32(74).string(message.createdAt);
    }
    if (message.createdBy !== "") {
      writer.uint32(82).string(message.createdBy);
    }
    if (message.expiresAt !== "") {
      writer.uint32(90).string(message.expiresAt);
    }
    if (message.updatedAt !== "") {
      writer.uint32(98).string(message.updatedAt);
    }
    if (message.updatedBy !== "") {
      writer.uint32(106).string(message.updatedBy);
    }
    if (message.externalMaintainer !== "") {
      writer.uint32(114).string(message.externalMaintainer);
    }
    if (message.policyUid !== "") {
      writer.uint32(122).string(message.policyUid);
    }
    if (message.extradata.length !== 0) {
      writer.uint32(130).bytes(message.extradata);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): EndpointAuthInfo {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseEndpointAuthInfo();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.uid = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.usergroupUid = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.usergroupName = reader.string();
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.usergroupEmail = reader.string();
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.endpointPattern = reader.string();
          continue;
        case 6:
          if (tag !== 48) {
            break;
          }

          message.granted = reader.bool();
          continue;
        case 7:
          if (tag !== 56) {
            break;
          }

          message.priority = reader.int32();
          continue;
        case 8:
          if (tag !== 66) {
            break;
          }

          message.reason = reader.string();
          continue;
        case 9:
          if (tag !== 74) {
            break;
          }

          message.createdAt = reader.string();
          continue;
        case 10:
          if (tag !== 82) {
            break;
          }

          message.createdBy = reader.string();
          continue;
        case 11:
          if (tag !== 90) {
            break;
          }

          message.expiresAt = reader.string();
          continue;
        case 12:
          if (tag !== 98) {
            break;
          }

          message.updatedAt = reader.string();
          continue;
        case 13:
          if (tag !== 106) {
            break;
          }

          message.updatedBy = reader.string();
          continue;
        case 14:
          if (tag !== 114) {
            break;
          }

          message.externalMaintainer = reader.string();
          continue;
        case 15:
          if (tag !== 122) {
            break;
          }

          message.policyUid = reader.string();
          continue;
        case 16:
          if (tag !== 130) {
            break;
          }

          message.extradata = reader.bytes();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): EndpointAuthInfo {
    return {
      uid: isSet(object.uid) ? globalThis.String(object.uid) : "",
      usergroupUid: isSet(object.usergroupUid) ? globalThis.String(object.usergroupUid) : "",
      usergroupName: isSet(object.usergroupName) ? globalThis.String(object.usergroupName) : "",
      usergroupEmail: isSet(object.usergroupEmail) ? globalThis.String(object.usergroupEmail) : "",
      endpointPattern: isSet(object.endpointPattern) ? globalThis.String(object.endpointPattern) : "",
      granted: isSet(object.granted) ? globalThis.Boolean(object.granted) : false,
      priority: isSet(object.priority) ? globalThis.Number(object.priority) : 0,
      reason: isSet(object.reason) ? globalThis.String(object.reason) : "",
      createdAt: isSet(object.createdAt) ? globalThis.String(object.createdAt) : "",
      createdBy: isSet(object.createdBy) ? globalThis.String(object.createdBy) : "",
      expiresAt: isSet(object.expiresAt) ? globalThis.String(object.expiresAt) : "",
      updatedAt: isSet(object.updatedAt) ? globalThis.String(object.updatedAt) : "",
      updatedBy: isSet(object.updatedBy) ? globalThis.String(object.updatedBy) : "",
      externalMaintainer: isSet(object.externalMaintainer) ? globalThis.String(object.externalMaintainer) : "",
      policyUid: isSet(object.policyUid) ? globalThis.String(object.policyUid) : "",
      extradata: isSet(object.extradata) ? bytesFromBase64(object.extradata) : new Uint8Array(0),
    };
  },

  toJSON(message: EndpointAuthInfo): unknown {
    const obj: any = {};
    if (message.uid !== "") {
      obj.uid = message.uid;
    }
    if (message.usergroupUid !== "") {
      obj.usergroupUid = message.usergroupUid;
    }
    if (message.usergroupName !== "") {
      obj.usergroupName = message.usergroupName;
    }
    if (message.usergroupEmail !== "") {
      obj.usergroupEmail = message.usergroupEmail;
    }
    if (message.endpointPattern !== "") {
      obj.endpointPattern = message.endpointPattern;
    }
    if (message.granted === true) {
      obj.granted = message.granted;
    }
    if (message.priority !== 0) {
      obj.priority = Math.round(message.priority);
    }
    if (message.reason !== "") {
      obj.reason = message.reason;
    }
    if (message.createdAt !== "") {
      obj.createdAt = message.createdAt;
    }
    if (message.createdBy !== "") {
      obj.createdBy = message.createdBy;
    }
    if (message.expiresAt !== "") {
      obj.expiresAt = message.expiresAt;
    }
    if (message.updatedAt !== "") {
      obj.updatedAt = message.updatedAt;
    }
    if (message.updatedBy !== "") {
      obj.updatedBy = message.updatedBy;
    }
    if (message.externalMaintainer !== "") {
      obj.externalMaintainer = message.externalMaintainer;
    }
    if (message.policyUid !== "") {
      obj.policyUid = message.policyUid;
    }
    if (message.extradata.length !== 0) {
      obj.extradata = base64FromBytes(message.extradata);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<EndpointAuthInfo>, I>>(base?: I): EndpointAuthInfo {
    return EndpointAuthInfo.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<EndpointAuthInfo>, I>>(object: I): EndpointAuthInfo {
    const message = createBaseEndpointAuthInfo();
    message.uid = object.uid ?? "";
    message.usergroupUid = object.usergroupUid ?? "";
    message.usergroupName = object.usergroupName ?? "";
    message.usergroupEmail = object.usergroupEmail ?? "";
    message.endpointPattern = object.endpointPattern ?? "";
    message.granted = object.granted ?? false;
    message.priority = object.priority ?? 0;
    message.reason = object.reason ?? "";
    message.createdAt = object.createdAt ?? "";
    message.createdBy = object.createdBy ?? "";
    message.expiresAt = object.expiresAt ?? "";
    message.updatedAt = object.updatedAt ?? "";
    message.updatedBy = object.updatedBy ?? "";
    message.externalMaintainer = object.externalMaintainer ?? "";
    message.policyUid = object.policyUid ?? "";
    message.extradata = object.extradata ?? new Uint8Array(0);
    return message;
  },
};

function createBaseTestEndpointAccessRequest(): TestEndpointAccessRequest {
  return { userIdentifier: "", endpointPattern: "", includeAllPolicies: false };
}

export const TestEndpointAccessRequest = {
  encode(message: TestEndpointAccessRequest, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.userIdentifier !== "") {
      writer.uint32(10).string(message.userIdentifier);
    }
    if (message.endpointPattern !== "") {
      writer.uint32(18).string(message.endpointPattern);
    }
    if (message.includeAllPolicies === true) {
      writer.uint32(24).bool(message.includeAllPolicies);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): TestEndpointAccessRequest {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseTestEndpointAccessRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.userIdentifier = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.endpointPattern = reader.string();
          continue;
        case 3:
          if (tag !== 24) {
            break;
          }

          message.includeAllPolicies = reader.bool();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): TestEndpointAccessRequest {
    return {
      userIdentifier: isSet(object.userIdentifier) ? globalThis.String(object.userIdentifier) : "",
      endpointPattern: isSet(object.endpointPattern) ? globalThis.String(object.endpointPattern) : "",
      includeAllPolicies: isSet(object.includeAllPolicies) ? globalThis.Boolean(object.includeAllPolicies) : false,
    };
  },

  toJSON(message: TestEndpointAccessRequest): unknown {
    const obj: any = {};
    if (message.userIdentifier !== "") {
      obj.userIdentifier = message.userIdentifier;
    }
    if (message.endpointPattern !== "") {
      obj.endpointPattern = message.endpointPattern;
    }
    if (message.includeAllPolicies === true) {
      obj.includeAllPolicies = message.includeAllPolicies;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<TestEndpointAccessRequest>, I>>(base?: I): TestEndpointAccessRequest {
    return TestEndpointAccessRequest.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<TestEndpointAccessRequest>, I>>(object: I): TestEndpointAccessRequest {
    const message = createBaseTestEndpointAccessRequest();
    message.userIdentifier = object.userIdentifier ?? "";
    message.endpointPattern = object.endpointPattern ?? "";
    message.includeAllPolicies = object.includeAllPolicies ?? false;
    return message;
  },
};

function createBasePolicyMatchInfo(): PolicyMatchInfo {
  return { policy: undefined, matched: false, specificity: 0, selected: false };
}

export const PolicyMatchInfo = {
  encode(message: PolicyMatchInfo, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.policy !== undefined) {
      EndpointAuthInfo.encode(message.policy, writer.uint32(10).fork()).ldelim();
    }
    if (message.matched === true) {
      writer.uint32(16).bool(message.matched);
    }
    if (message.specificity !== 0) {
      writer.uint32(24).int32(message.specificity);
    }
    if (message.selected === true) {
      writer.uint32(32).bool(message.selected);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): PolicyMatchInfo {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBasePolicyMatchInfo();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.policy = EndpointAuthInfo.decode(reader, reader.uint32());
          continue;
        case 2:
          if (tag !== 16) {
            break;
          }

          message.matched = reader.bool();
          continue;
        case 3:
          if (tag !== 24) {
            break;
          }

          message.specificity = reader.int32();
          continue;
        case 4:
          if (tag !== 32) {
            break;
          }

          message.selected = reader.bool();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): PolicyMatchInfo {
    return {
      policy: isSet(object.policy) ? EndpointAuthInfo.fromJSON(object.policy) : undefined,
      matched: isSet(object.matched) ? globalThis.Boolean(object.matched) : false,
      specificity: isSet(object.specificity) ? globalThis.Number(object.specificity) : 0,
      selected: isSet(object.selected) ? globalThis.Boolean(object.selected) : false,
    };
  },

  toJSON(message: PolicyMatchInfo): unknown {
    const obj: any = {};
    if (message.policy !== undefined) {
      obj.policy = EndpointAuthInfo.toJSON(message.policy);
    }
    if (message.matched === true) {
      obj.matched = message.matched;
    }
    if (message.specificity !== 0) {
      obj.specificity = Math.round(message.specificity);
    }
    if (message.selected === true) {
      obj.selected = message.selected;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<PolicyMatchInfo>, I>>(base?: I): PolicyMatchInfo {
    return PolicyMatchInfo.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<PolicyMatchInfo>, I>>(object: I): PolicyMatchInfo {
    const message = createBasePolicyMatchInfo();
    message.policy = (object.policy !== undefined && object.policy !== null)
      ? EndpointAuthInfo.fromPartial(object.policy)
      : undefined;
    message.matched = object.matched ?? false;
    message.specificity = object.specificity ?? 0;
    message.selected = object.selected ?? false;
    return message;
  },
};

function createBaseTestEndpointAccessResponse(): TestEndpointAccessResponse {
  return {
    allowed: false,
    reason: "",
    userUid: "",
    userEmail: "",
    userName: "",
    userGroupNames: [],
    userGroupUids: [],
    winningPolicy: undefined,
    matchedPolicies: [],
    evaluatedPolicies: [],
    noPolicyMatched: false,
  };
}

export const TestEndpointAccessResponse = {
  encode(message: TestEndpointAccessResponse, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.allowed === true) {
      writer.uint32(8).bool(message.allowed);
    }
    if (message.reason !== "") {
      writer.uint32(18).string(message.reason);
    }
    if (message.userUid !== "") {
      writer.uint32(26).string(message.userUid);
    }
    if (message.userEmail !== "") {
      writer.uint32(34).string(message.userEmail);
    }
    if (message.userName !== "") {
      writer.uint32(42).string(message.userName);
    }
    for (const v of message.userGroupNames) {
      writer.uint32(50).string(v!);
    }
    for (const v of message.userGroupUids) {
      writer.uint32(58).string(v!);
    }
    if (message.winningPolicy !== undefined) {
      PolicyMatchInfo.encode(message.winningPolicy, writer.uint32(66).fork()).ldelim();
    }
    for (const v of message.matchedPolicies) {
      PolicyMatchInfo.encode(v!, writer.uint32(74).fork()).ldelim();
    }
    for (const v of message.evaluatedPolicies) {
      PolicyMatchInfo.encode(v!, writer.uint32(82).fork()).ldelim();
    }
    if (message.noPolicyMatched === true) {
      writer.uint32(88).bool(message.noPolicyMatched);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): TestEndpointAccessResponse {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseTestEndpointAccessResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.allowed = reader.bool();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.reason = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.userUid = reader.string();
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.userEmail = reader.string();
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.userName = reader.string();
          continue;
        case 6:
          if (tag !== 50) {
            break;
          }

          message.userGroupNames.push(reader.string());
          continue;
        case 7:
          if (tag !== 58) {
            break;
          }

          message.userGroupUids.push(reader.string());
          continue;
        case 8:
          if (tag !== 66) {
            break;
          }

          message.winningPolicy = PolicyMatchInfo.decode(reader, reader.uint32());
          continue;
        case 9:
          if (tag !== 74) {
            break;
          }

          message.matchedPolicies.push(PolicyMatchInfo.decode(reader, reader.uint32()));
          continue;
        case 10:
          if (tag !== 82) {
            break;
          }

          message.evaluatedPolicies.push(PolicyMatchInfo.decode(reader, reader.uint32()));
          continue;
        case 11:
          if (tag !== 88) {
            break;
          }

          message.noPolicyMatched = reader.bool();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): TestEndpointAccessResponse {
    return {
      allowed: isSet(object.allowed) ? globalThis.Boolean(object.allowed) : false,
      reason: isSet(object.reason) ? globalThis.String(object.reason) : "",
      userUid: isSet(object.userUid) ? globalThis.String(object.userUid) : "",
      userEmail: isSet(object.userEmail) ? globalThis.String(object.userEmail) : "",
      userName: isSet(object.userName) ? globalThis.String(object.userName) : "",
      userGroupNames: globalThis.Array.isArray(object?.userGroupNames)
        ? object.userGroupNames.map((e: any) => globalThis.String(e))
        : [],
      userGroupUids: globalThis.Array.isArray(object?.userGroupUids)
        ? object.userGroupUids.map((e: any) => globalThis.String(e))
        : [],
      winningPolicy: isSet(object.winningPolicy) ? PolicyMatchInfo.fromJSON(object.winningPolicy) : undefined,
      matchedPolicies: globalThis.Array.isArray(object?.matchedPolicies)
        ? object.matchedPolicies.map((e: any) => PolicyMatchInfo.fromJSON(e))
        : [],
      evaluatedPolicies: globalThis.Array.isArray(object?.evaluatedPolicies)
        ? object.evaluatedPolicies.map((e: any) => PolicyMatchInfo.fromJSON(e))
        : [],
      noPolicyMatched: isSet(object.noPolicyMatched) ? globalThis.Boolean(object.noPolicyMatched) : false,
    };
  },

  toJSON(message: TestEndpointAccessResponse): unknown {
    const obj: any = {};
    if (message.allowed === true) {
      obj.allowed = message.allowed;
    }
    if (message.reason !== "") {
      obj.reason = message.reason;
    }
    if (message.userUid !== "") {
      obj.userUid = message.userUid;
    }
    if (message.userEmail !== "") {
      obj.userEmail = message.userEmail;
    }
    if (message.userName !== "") {
      obj.userName = message.userName;
    }
    if (message.userGroupNames?.length) {
      obj.userGroupNames = message.userGroupNames;
    }
    if (message.userGroupUids?.length) {
      obj.userGroupUids = message.userGroupUids;
    }
    if (message.winningPolicy !== undefined) {
      obj.winningPolicy = PolicyMatchInfo.toJSON(message.winningPolicy);
    }
    if (message.matchedPolicies?.length) {
      obj.matchedPolicies = message.matchedPolicies.map((e) => PolicyMatchInfo.toJSON(e));
    }
    if (message.evaluatedPolicies?.length) {
      obj.evaluatedPolicies = message.evaluatedPolicies.map((e) => PolicyMatchInfo.toJSON(e));
    }
    if (message.noPolicyMatched === true) {
      obj.noPolicyMatched = message.noPolicyMatched;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<TestEndpointAccessResponse>, I>>(base?: I): TestEndpointAccessResponse {
    return TestEndpointAccessResponse.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<TestEndpointAccessResponse>, I>>(object: I): TestEndpointAccessResponse {
    const message = createBaseTestEndpointAccessResponse();
    message.allowed = object.allowed ?? false;
    message.reason = object.reason ?? "";
    message.userUid = object.userUid ?? "";
    message.userEmail = object.userEmail ?? "";
    message.userName = object.userName ?? "";
    message.userGroupNames = object.userGroupNames?.map((e) => e) || [];
    message.userGroupUids = object.userGroupUids?.map((e) => e) || [];
    message.winningPolicy = (object.winningPolicy !== undefined && object.winningPolicy !== null)
      ? PolicyMatchInfo.fromPartial(object.winningPolicy)
      : undefined;
    message.matchedPolicies = object.matchedPolicies?.map((e) => PolicyMatchInfo.fromPartial(e)) || [];
    message.evaluatedPolicies = object.evaluatedPolicies?.map((e) => PolicyMatchInfo.fromPartial(e)) || [];
    message.noPolicyMatched = object.noPolicyMatched ?? false;
    return message;
  },
};

function createBaseSyncPoliciesRequest(): SyncPoliciesRequest {
  return { dryRun: false, verifyOnly: false, policiesJson: "", groupsJson: "" };
}

export const SyncPoliciesRequest = {
  encode(message: SyncPoliciesRequest, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.dryRun === true) {
      writer.uint32(8).bool(message.dryRun);
    }
    if (message.verifyOnly === true) {
      writer.uint32(16).bool(message.verifyOnly);
    }
    if (message.policiesJson !== "") {
      writer.uint32(26).string(message.policiesJson);
    }
    if (message.groupsJson !== "") {
      writer.uint32(34).string(message.groupsJson);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): SyncPoliciesRequest {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseSyncPoliciesRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.dryRun = reader.bool();
          continue;
        case 2:
          if (tag !== 16) {
            break;
          }

          message.verifyOnly = reader.bool();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.policiesJson = reader.string();
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.groupsJson = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): SyncPoliciesRequest {
    return {
      dryRun: isSet(object.dryRun) ? globalThis.Boolean(object.dryRun) : false,
      verifyOnly: isSet(object.verifyOnly) ? globalThis.Boolean(object.verifyOnly) : false,
      policiesJson: isSet(object.policiesJson) ? globalThis.String(object.policiesJson) : "",
      groupsJson: isSet(object.groupsJson) ? globalThis.String(object.groupsJson) : "",
    };
  },

  toJSON(message: SyncPoliciesRequest): unknown {
    const obj: any = {};
    if (message.dryRun === true) {
      obj.dryRun = message.dryRun;
    }
    if (message.verifyOnly === true) {
      obj.verifyOnly = message.verifyOnly;
    }
    if (message.policiesJson !== "") {
      obj.policiesJson = message.policiesJson;
    }
    if (message.groupsJson !== "") {
      obj.groupsJson = message.groupsJson;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<SyncPoliciesRequest>, I>>(base?: I): SyncPoliciesRequest {
    return SyncPoliciesRequest.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<SyncPoliciesRequest>, I>>(object: I): SyncPoliciesRequest {
    const message = createBaseSyncPoliciesRequest();
    message.dryRun = object.dryRun ?? false;
    message.verifyOnly = object.verifyOnly ?? false;
    message.policiesJson = object.policiesJson ?? "";
    message.groupsJson = object.groupsJson ?? "";
    return message;
  },
};

function createBaseSyncPoliciesResponse(): SyncPoliciesResponse {
  return {
    success: false,
    message: "",
    groupsAdded: 0,
    groupsUpdated: 0,
    policiesAdded: 0,
    policiesUpdated: 0,
    policiesDeleted: 0,
    errors: [],
  };
}

export const SyncPoliciesResponse = {
  encode(message: SyncPoliciesResponse, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.success === true) {
      writer.uint32(8).bool(message.success);
    }
    if (message.message !== "") {
      writer.uint32(18).string(message.message);
    }
    if (message.groupsAdded !== 0) {
      writer.uint32(24).int32(message.groupsAdded);
    }
    if (message.groupsUpdated !== 0) {
      writer.uint32(32).int32(message.groupsUpdated);
    }
    if (message.policiesAdded !== 0) {
      writer.uint32(40).int32(message.policiesAdded);
    }
    if (message.policiesUpdated !== 0) {
      writer.uint32(48).int32(message.policiesUpdated);
    }
    if (message.policiesDeleted !== 0) {
      writer.uint32(56).int32(message.policiesDeleted);
    }
    for (const v of message.errors) {
      writer.uint32(66).string(v!);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): SyncPoliciesResponse {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseSyncPoliciesResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.success = reader.bool();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.message = reader.string();
          continue;
        case 3:
          if (tag !== 24) {
            break;
          }

          message.groupsAdded = reader.int32();
          continue;
        case 4:
          if (tag !== 32) {
            break;
          }

          message.groupsUpdated = reader.int32();
          continue;
        case 5:
          if (tag !== 40) {
            break;
          }

          message.policiesAdded = reader.int32();
          continue;
        case 6:
          if (tag !== 48) {
            break;
          }

          message.policiesUpdated = reader.int32();
          continue;
        case 7:
          if (tag !== 56) {
            break;
          }

          message.policiesDeleted = reader.int32();
          continue;
        case 8:
          if (tag !== 66) {
            break;
          }

          message.errors.push(reader.string());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): SyncPoliciesResponse {
    return {
      success: isSet(object.success) ? globalThis.Boolean(object.success) : false,
      message: isSet(object.message) ? globalThis.String(object.message) : "",
      groupsAdded: isSet(object.groupsAdded) ? globalThis.Number(object.groupsAdded) : 0,
      groupsUpdated: isSet(object.groupsUpdated) ? globalThis.Number(object.groupsUpdated) : 0,
      policiesAdded: isSet(object.policiesAdded) ? globalThis.Number(object.policiesAdded) : 0,
      policiesUpdated: isSet(object.policiesUpdated) ? globalThis.Number(object.policiesUpdated) : 0,
      policiesDeleted: isSet(object.policiesDeleted) ? globalThis.Number(object.policiesDeleted) : 0,
      errors: globalThis.Array.isArray(object?.errors) ? object.errors.map((e: any) => globalThis.String(e)) : [],
    };
  },

  toJSON(message: SyncPoliciesResponse): unknown {
    const obj: any = {};
    if (message.success === true) {
      obj.success = message.success;
    }
    if (message.message !== "") {
      obj.message = message.message;
    }
    if (message.groupsAdded !== 0) {
      obj.groupsAdded = Math.round(message.groupsAdded);
    }
    if (message.groupsUpdated !== 0) {
      obj.groupsUpdated = Math.round(message.groupsUpdated);
    }
    if (message.policiesAdded !== 0) {
      obj.policiesAdded = Math.round(message.policiesAdded);
    }
    if (message.policiesUpdated !== 0) {
      obj.policiesUpdated = Math.round(message.policiesUpdated);
    }
    if (message.policiesDeleted !== 0) {
      obj.policiesDeleted = Math.round(message.policiesDeleted);
    }
    if (message.errors?.length) {
      obj.errors = message.errors;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<SyncPoliciesResponse>, I>>(base?: I): SyncPoliciesResponse {
    return SyncPoliciesResponse.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<SyncPoliciesResponse>, I>>(object: I): SyncPoliciesResponse {
    const message = createBaseSyncPoliciesResponse();
    message.success = object.success ?? false;
    message.message = object.message ?? "";
    message.groupsAdded = object.groupsAdded ?? 0;
    message.groupsUpdated = object.groupsUpdated ?? 0;
    message.policiesAdded = object.policiesAdded ?? 0;
    message.policiesUpdated = object.policiesUpdated ?? 0;
    message.policiesDeleted = object.policiesDeleted ?? 0;
    message.errors = object.errors?.map((e) => e) || [];
    return message;
  },
};

function createBasePolicyDefinition(): PolicyDefinition {
  return { usergroupName: "", endpointPattern: "", granted: false, priority: 0, reason: "", extraData: {} };
}

export const PolicyDefinition = {
  encode(message: PolicyDefinition, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.usergroupName !== "") {
      writer.uint32(10).string(message.usergroupName);
    }
    if (message.endpointPattern !== "") {
      writer.uint32(18).string(message.endpointPattern);
    }
    if (message.granted === true) {
      writer.uint32(24).bool(message.granted);
    }
    if (message.priority !== 0) {
      writer.uint32(32).int32(message.priority);
    }
    if (message.reason !== "") {
      writer.uint32(42).string(message.reason);
    }
    Object.entries(message.extraData).forEach(([key, value]) => {
      PolicyDefinition_ExtraDataEntry.encode({ key: key as any, value }, writer.uint32(50).fork()).ldelim();
    });
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): PolicyDefinition {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBasePolicyDefinition();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.usergroupName = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.endpointPattern = reader.string();
          continue;
        case 3:
          if (tag !== 24) {
            break;
          }

          message.granted = reader.bool();
          continue;
        case 4:
          if (tag !== 32) {
            break;
          }

          message.priority = reader.int32();
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.reason = reader.string();
          continue;
        case 6:
          if (tag !== 50) {
            break;
          }

          const entry6 = PolicyDefinition_ExtraDataEntry.decode(reader, reader.uint32());
          if (entry6.value !== undefined) {
            message.extraData[entry6.key] = entry6.value;
          }
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): PolicyDefinition {
    return {
      usergroupName: isSet(object.usergroupName) ? globalThis.String(object.usergroupName) : "",
      endpointPattern: isSet(object.endpointPattern) ? globalThis.String(object.endpointPattern) : "",
      granted: isSet(object.granted) ? globalThis.Boolean(object.granted) : false,
      priority: isSet(object.priority) ? globalThis.Number(object.priority) : 0,
      reason: isSet(object.reason) ? globalThis.String(object.reason) : "",
      extraData: isObject(object.extraData)
        ? Object.entries(object.extraData).reduce<{ [key: string]: string }>((acc, [key, value]) => {
          acc[key] = String(value);
          return acc;
        }, {})
        : {},
    };
  },

  toJSON(message: PolicyDefinition): unknown {
    const obj: any = {};
    if (message.usergroupName !== "") {
      obj.usergroupName = message.usergroupName;
    }
    if (message.endpointPattern !== "") {
      obj.endpointPattern = message.endpointPattern;
    }
    if (message.granted === true) {
      obj.granted = message.granted;
    }
    if (message.priority !== 0) {
      obj.priority = Math.round(message.priority);
    }
    if (message.reason !== "") {
      obj.reason = message.reason;
    }
    if (message.extraData) {
      const entries = Object.entries(message.extraData);
      if (entries.length > 0) {
        obj.extraData = {};
        entries.forEach(([k, v]) => {
          obj.extraData[k] = v;
        });
      }
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<PolicyDefinition>, I>>(base?: I): PolicyDefinition {
    return PolicyDefinition.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<PolicyDefinition>, I>>(object: I): PolicyDefinition {
    const message = createBasePolicyDefinition();
    message.usergroupName = object.usergroupName ?? "";
    message.endpointPattern = object.endpointPattern ?? "";
    message.granted = object.granted ?? false;
    message.priority = object.priority ?? 0;
    message.reason = object.reason ?? "";
    message.extraData = Object.entries(object.extraData ?? {}).reduce<{ [key: string]: string }>(
      (acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = globalThis.String(value);
        }
        return acc;
      },
      {},
    );
    return message;
  },
};

function createBasePolicyDefinition_ExtraDataEntry(): PolicyDefinition_ExtraDataEntry {
  return { key: "", value: "" };
}

export const PolicyDefinition_ExtraDataEntry = {
  encode(message: PolicyDefinition_ExtraDataEntry, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.key !== "") {
      writer.uint32(10).string(message.key);
    }
    if (message.value !== "") {
      writer.uint32(18).string(message.value);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): PolicyDefinition_ExtraDataEntry {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBasePolicyDefinition_ExtraDataEntry();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.key = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.value = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): PolicyDefinition_ExtraDataEntry {
    return {
      key: isSet(object.key) ? globalThis.String(object.key) : "",
      value: isSet(object.value) ? globalThis.String(object.value) : "",
    };
  },

  toJSON(message: PolicyDefinition_ExtraDataEntry): unknown {
    const obj: any = {};
    if (message.key !== "") {
      obj.key = message.key;
    }
    if (message.value !== "") {
      obj.value = message.value;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<PolicyDefinition_ExtraDataEntry>, I>>(base?: I): PolicyDefinition_ExtraDataEntry {
    return PolicyDefinition_ExtraDataEntry.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<PolicyDefinition_ExtraDataEntry>, I>>(
    object: I,
  ): PolicyDefinition_ExtraDataEntry {
    const message = createBasePolicyDefinition_ExtraDataEntry();
    message.key = object.key ?? "";
    message.value = object.value ?? "";
    return message;
  },
};

function createBaseGroupDefinition(): GroupDefinition {
  return { name: "", description: "", members: [] };
}

export const GroupDefinition = {
  encode(message: GroupDefinition, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.name !== "") {
      writer.uint32(10).string(message.name);
    }
    if (message.description !== "") {
      writer.uint32(18).string(message.description);
    }
    for (const v of message.members) {
      writer.uint32(26).string(v!);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): GroupDefinition {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseGroupDefinition();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.name = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.description = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.members.push(reader.string());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): GroupDefinition {
    return {
      name: isSet(object.name) ? globalThis.String(object.name) : "",
      description: isSet(object.description) ? globalThis.String(object.description) : "",
      members: globalThis.Array.isArray(object?.members) ? object.members.map((e: any) => globalThis.String(e)) : [],
    };
  },

  toJSON(message: GroupDefinition): unknown {
    const obj: any = {};
    if (message.name !== "") {
      obj.name = message.name;
    }
    if (message.description !== "") {
      obj.description = message.description;
    }
    if (message.members?.length) {
      obj.members = message.members;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<GroupDefinition>, I>>(base?: I): GroupDefinition {
    return GroupDefinition.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<GroupDefinition>, I>>(object: I): GroupDefinition {
    const message = createBaseGroupDefinition();
    message.name = object.name ?? "";
    message.description = object.description ?? "";
    message.members = object.members?.map((e) => e) || [];
    return message;
  },
};

function createBaseTableAccessRule(): TableAccessRule {
  return {
    usergroupName: "",
    tableName: "",
    accessModes: [],
    granted: false,
    priority: 0,
    reason: "",
    rowFilter: "",
    terminal: false,
  };
}

export const TableAccessRule = {
  encode(message: TableAccessRule, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.usergroupName !== "") {
      writer.uint32(10).string(message.usergroupName);
    }
    if (message.tableName !== "") {
      writer.uint32(18).string(message.tableName);
    }
    for (const v of message.accessModes) {
      writer.uint32(26).string(v!);
    }
    if (message.granted === true) {
      writer.uint32(32).bool(message.granted);
    }
    if (message.priority !== 0) {
      writer.uint32(40).int32(message.priority);
    }
    if (message.reason !== "") {
      writer.uint32(50).string(message.reason);
    }
    if (message.rowFilter !== "") {
      writer.uint32(58).string(message.rowFilter);
    }
    if (message.terminal === true) {
      writer.uint32(64).bool(message.terminal);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): TableAccessRule {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseTableAccessRule();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.usergroupName = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.tableName = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.accessModes.push(reader.string());
          continue;
        case 4:
          if (tag !== 32) {
            break;
          }

          message.granted = reader.bool();
          continue;
        case 5:
          if (tag !== 40) {
            break;
          }

          message.priority = reader.int32();
          continue;
        case 6:
          if (tag !== 50) {
            break;
          }

          message.reason = reader.string();
          continue;
        case 7:
          if (tag !== 58) {
            break;
          }

          message.rowFilter = reader.string();
          continue;
        case 8:
          if (tag !== 64) {
            break;
          }

          message.terminal = reader.bool();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): TableAccessRule {
    return {
      usergroupName: isSet(object.usergroupName) ? globalThis.String(object.usergroupName) : "",
      tableName: isSet(object.tableName) ? globalThis.String(object.tableName) : "",
      accessModes: globalThis.Array.isArray(object?.accessModes)
        ? object.accessModes.map((e: any) => globalThis.String(e))
        : [],
      granted: isSet(object.granted) ? globalThis.Boolean(object.granted) : false,
      priority: isSet(object.priority) ? globalThis.Number(object.priority) : 0,
      reason: isSet(object.reason) ? globalThis.String(object.reason) : "",
      rowFilter: isSet(object.rowFilter) ? globalThis.String(object.rowFilter) : "",
      terminal: isSet(object.terminal) ? globalThis.Boolean(object.terminal) : false,
    };
  },

  toJSON(message: TableAccessRule): unknown {
    const obj: any = {};
    if (message.usergroupName !== "") {
      obj.usergroupName = message.usergroupName;
    }
    if (message.tableName !== "") {
      obj.tableName = message.tableName;
    }
    if (message.accessModes?.length) {
      obj.accessModes = message.accessModes;
    }
    if (message.granted === true) {
      obj.granted = message.granted;
    }
    if (message.priority !== 0) {
      obj.priority = Math.round(message.priority);
    }
    if (message.reason !== "") {
      obj.reason = message.reason;
    }
    if (message.rowFilter !== "") {
      obj.rowFilter = message.rowFilter;
    }
    if (message.terminal === true) {
      obj.terminal = message.terminal;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<TableAccessRule>, I>>(base?: I): TableAccessRule {
    return TableAccessRule.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<TableAccessRule>, I>>(object: I): TableAccessRule {
    const message = createBaseTableAccessRule();
    message.usergroupName = object.usergroupName ?? "";
    message.tableName = object.tableName ?? "";
    message.accessModes = object.accessModes?.map((e) => e) || [];
    message.granted = object.granted ?? false;
    message.priority = object.priority ?? 0;
    message.reason = object.reason ?? "";
    message.rowFilter = object.rowFilter ?? "";
    message.terminal = object.terminal ?? false;
    return message;
  },
};

function createBasePolicy(): Policy {
  return { name: "", version: "", description: "", groups: [], policies: [], metadata: {}, tableAccess: [] };
}

export const Policy = {
  encode(message: Policy, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.name !== "") {
      writer.uint32(10).string(message.name);
    }
    if (message.version !== "") {
      writer.uint32(18).string(message.version);
    }
    if (message.description !== "") {
      writer.uint32(26).string(message.description);
    }
    for (const v of message.groups) {
      GroupDefinition.encode(v!, writer.uint32(34).fork()).ldelim();
    }
    for (const v of message.policies) {
      PolicyDefinition.encode(v!, writer.uint32(42).fork()).ldelim();
    }
    Object.entries(message.metadata).forEach(([key, value]) => {
      Policy_MetadataEntry.encode({ key: key as any, value }, writer.uint32(50).fork()).ldelim();
    });
    for (const v of message.tableAccess) {
      TableAccessRule.encode(v!, writer.uint32(58).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Policy {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBasePolicy();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.name = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.version = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.description = reader.string();
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.groups.push(GroupDefinition.decode(reader, reader.uint32()));
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.policies.push(PolicyDefinition.decode(reader, reader.uint32()));
          continue;
        case 6:
          if (tag !== 50) {
            break;
          }

          const entry6 = Policy_MetadataEntry.decode(reader, reader.uint32());
          if (entry6.value !== undefined) {
            message.metadata[entry6.key] = entry6.value;
          }
          continue;
        case 7:
          if (tag !== 58) {
            break;
          }

          message.tableAccess.push(TableAccessRule.decode(reader, reader.uint32()));
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): Policy {
    return {
      name: isSet(object.name) ? globalThis.String(object.name) : "",
      version: isSet(object.version) ? globalThis.String(object.version) : "",
      description: isSet(object.description) ? globalThis.String(object.description) : "",
      groups: globalThis.Array.isArray(object?.groups)
        ? object.groups.map((e: any) => GroupDefinition.fromJSON(e))
        : [],
      policies: globalThis.Array.isArray(object?.policies)
        ? object.policies.map((e: any) => PolicyDefinition.fromJSON(e))
        : [],
      metadata: isObject(object.metadata)
        ? Object.entries(object.metadata).reduce<{ [key: string]: string }>((acc, [key, value]) => {
          acc[key] = String(value);
          return acc;
        }, {})
        : {},
      tableAccess: globalThis.Array.isArray(object?.tableAccess)
        ? object.tableAccess.map((e: any) => TableAccessRule.fromJSON(e))
        : [],
    };
  },

  toJSON(message: Policy): unknown {
    const obj: any = {};
    if (message.name !== "") {
      obj.name = message.name;
    }
    if (message.version !== "") {
      obj.version = message.version;
    }
    if (message.description !== "") {
      obj.description = message.description;
    }
    if (message.groups?.length) {
      obj.groups = message.groups.map((e) => GroupDefinition.toJSON(e));
    }
    if (message.policies?.length) {
      obj.policies = message.policies.map((e) => PolicyDefinition.toJSON(e));
    }
    if (message.metadata) {
      const entries = Object.entries(message.metadata);
      if (entries.length > 0) {
        obj.metadata = {};
        entries.forEach(([k, v]) => {
          obj.metadata[k] = v;
        });
      }
    }
    if (message.tableAccess?.length) {
      obj.tableAccess = message.tableAccess.map((e) => TableAccessRule.toJSON(e));
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<Policy>, I>>(base?: I): Policy {
    return Policy.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<Policy>, I>>(object: I): Policy {
    const message = createBasePolicy();
    message.name = object.name ?? "";
    message.version = object.version ?? "";
    message.description = object.description ?? "";
    message.groups = object.groups?.map((e) => GroupDefinition.fromPartial(e)) || [];
    message.policies = object.policies?.map((e) => PolicyDefinition.fromPartial(e)) || [];
    message.metadata = Object.entries(object.metadata ?? {}).reduce<{ [key: string]: string }>((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = globalThis.String(value);
      }
      return acc;
    }, {});
    message.tableAccess = object.tableAccess?.map((e) => TableAccessRule.fromPartial(e)) || [];
    return message;
  },
};

function createBasePolicy_MetadataEntry(): Policy_MetadataEntry {
  return { key: "", value: "" };
}

export const Policy_MetadataEntry = {
  encode(message: Policy_MetadataEntry, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.key !== "") {
      writer.uint32(10).string(message.key);
    }
    if (message.value !== "") {
      writer.uint32(18).string(message.value);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Policy_MetadataEntry {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBasePolicy_MetadataEntry();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.key = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.value = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): Policy_MetadataEntry {
    return {
      key: isSet(object.key) ? globalThis.String(object.key) : "",
      value: isSet(object.value) ? globalThis.String(object.value) : "",
    };
  },

  toJSON(message: Policy_MetadataEntry): unknown {
    const obj: any = {};
    if (message.key !== "") {
      obj.key = message.key;
    }
    if (message.value !== "") {
      obj.value = message.value;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<Policy_MetadataEntry>, I>>(base?: I): Policy_MetadataEntry {
    return Policy_MetadataEntry.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<Policy_MetadataEntry>, I>>(object: I): Policy_MetadataEntry {
    const message = createBasePolicy_MetadataEntry();
    message.key = object.key ?? "";
    message.value = object.value ?? "";
    return message;
  },
};

function createBasePolicyInfo(): PolicyInfo {
  return {
    uid: "",
    name: "",
    policy: undefined,
    createdAt: "",
    createdBy: "",
    updatedAt: "",
    updatedBy: "",
    active: false,
  };
}

export const PolicyInfo = {
  encode(message: PolicyInfo, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.uid !== "") {
      writer.uint32(10).string(message.uid);
    }
    if (message.name !== "") {
      writer.uint32(18).string(message.name);
    }
    if (message.policy !== undefined) {
      Policy.encode(message.policy, writer.uint32(26).fork()).ldelim();
    }
    if (message.createdAt !== "") {
      writer.uint32(34).string(message.createdAt);
    }
    if (message.createdBy !== "") {
      writer.uint32(42).string(message.createdBy);
    }
    if (message.updatedAt !== "") {
      writer.uint32(50).string(message.updatedAt);
    }
    if (message.updatedBy !== "") {
      writer.uint32(58).string(message.updatedBy);
    }
    if (message.active === true) {
      writer.uint32(64).bool(message.active);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): PolicyInfo {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBasePolicyInfo();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.uid = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.name = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.policy = Policy.decode(reader, reader.uint32());
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.createdAt = reader.string();
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.createdBy = reader.string();
          continue;
        case 6:
          if (tag !== 50) {
            break;
          }

          message.updatedAt = reader.string();
          continue;
        case 7:
          if (tag !== 58) {
            break;
          }

          message.updatedBy = reader.string();
          continue;
        case 8:
          if (tag !== 64) {
            break;
          }

          message.active = reader.bool();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): PolicyInfo {
    return {
      uid: isSet(object.uid) ? globalThis.String(object.uid) : "",
      name: isSet(object.name) ? globalThis.String(object.name) : "",
      policy: isSet(object.policy) ? Policy.fromJSON(object.policy) : undefined,
      createdAt: isSet(object.createdAt) ? globalThis.String(object.createdAt) : "",
      createdBy: isSet(object.createdBy) ? globalThis.String(object.createdBy) : "",
      updatedAt: isSet(object.updatedAt) ? globalThis.String(object.updatedAt) : "",
      updatedBy: isSet(object.updatedBy) ? globalThis.String(object.updatedBy) : "",
      active: isSet(object.active) ? globalThis.Boolean(object.active) : false,
    };
  },

  toJSON(message: PolicyInfo): unknown {
    const obj: any = {};
    if (message.uid !== "") {
      obj.uid = message.uid;
    }
    if (message.name !== "") {
      obj.name = message.name;
    }
    if (message.policy !== undefined) {
      obj.policy = Policy.toJSON(message.policy);
    }
    if (message.createdAt !== "") {
      obj.createdAt = message.createdAt;
    }
    if (message.createdBy !== "") {
      obj.createdBy = message.createdBy;
    }
    if (message.updatedAt !== "") {
      obj.updatedAt = message.updatedAt;
    }
    if (message.updatedBy !== "") {
      obj.updatedBy = message.updatedBy;
    }
    if (message.active === true) {
      obj.active = message.active;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<PolicyInfo>, I>>(base?: I): PolicyInfo {
    return PolicyInfo.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<PolicyInfo>, I>>(object: I): PolicyInfo {
    const message = createBasePolicyInfo();
    message.uid = object.uid ?? "";
    message.name = object.name ?? "";
    message.policy = (object.policy !== undefined && object.policy !== null)
      ? Policy.fromPartial(object.policy)
      : undefined;
    message.createdAt = object.createdAt ?? "";
    message.createdBy = object.createdBy ?? "";
    message.updatedAt = object.updatedAt ?? "";
    message.updatedBy = object.updatedBy ?? "";
    message.active = object.active ?? false;
    return message;
  },
};

function createBaseUpsertPolicyRequest(): UpsertPolicyRequest {
  return { policy: undefined, createdBy: "" };
}

export const UpsertPolicyRequest = {
  encode(message: UpsertPolicyRequest, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.policy !== undefined) {
      Policy.encode(message.policy, writer.uint32(10).fork()).ldelim();
    }
    if (message.createdBy !== "") {
      writer.uint32(18).string(message.createdBy);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): UpsertPolicyRequest {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseUpsertPolicyRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.policy = Policy.decode(reader, reader.uint32());
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.createdBy = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): UpsertPolicyRequest {
    return {
      policy: isSet(object.policy) ? Policy.fromJSON(object.policy) : undefined,
      createdBy: isSet(object.createdBy) ? globalThis.String(object.createdBy) : "",
    };
  },

  toJSON(message: UpsertPolicyRequest): unknown {
    const obj: any = {};
    if (message.policy !== undefined) {
      obj.policy = Policy.toJSON(message.policy);
    }
    if (message.createdBy !== "") {
      obj.createdBy = message.createdBy;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<UpsertPolicyRequest>, I>>(base?: I): UpsertPolicyRequest {
    return UpsertPolicyRequest.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<UpsertPolicyRequest>, I>>(object: I): UpsertPolicyRequest {
    const message = createBaseUpsertPolicyRequest();
    message.policy = (object.policy !== undefined && object.policy !== null)
      ? Policy.fromPartial(object.policy)
      : undefined;
    message.createdBy = object.createdBy ?? "";
    return message;
  },
};

function createBaseUpsertPolicyResponse(): UpsertPolicyResponse {
  return { success: false, message: "", policyUid: "" };
}

export const UpsertPolicyResponse = {
  encode(message: UpsertPolicyResponse, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.success === true) {
      writer.uint32(8).bool(message.success);
    }
    if (message.message !== "") {
      writer.uint32(18).string(message.message);
    }
    if (message.policyUid !== "") {
      writer.uint32(26).string(message.policyUid);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): UpsertPolicyResponse {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseUpsertPolicyResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.success = reader.bool();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.message = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.policyUid = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): UpsertPolicyResponse {
    return {
      success: isSet(object.success) ? globalThis.Boolean(object.success) : false,
      message: isSet(object.message) ? globalThis.String(object.message) : "",
      policyUid: isSet(object.policyUid) ? globalThis.String(object.policyUid) : "",
    };
  },

  toJSON(message: UpsertPolicyResponse): unknown {
    const obj: any = {};
    if (message.success === true) {
      obj.success = message.success;
    }
    if (message.message !== "") {
      obj.message = message.message;
    }
    if (message.policyUid !== "") {
      obj.policyUid = message.policyUid;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<UpsertPolicyResponse>, I>>(base?: I): UpsertPolicyResponse {
    return UpsertPolicyResponse.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<UpsertPolicyResponse>, I>>(object: I): UpsertPolicyResponse {
    const message = createBaseUpsertPolicyResponse();
    message.success = object.success ?? false;
    message.message = object.message ?? "";
    message.policyUid = object.policyUid ?? "";
    return message;
  },
};

function createBaseListPoliciesRequest(): ListPoliciesRequest {
  return { activeOnly: false };
}

export const ListPoliciesRequest = {
  encode(message: ListPoliciesRequest, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.activeOnly === true) {
      writer.uint32(8).bool(message.activeOnly);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ListPoliciesRequest {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseListPoliciesRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.activeOnly = reader.bool();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ListPoliciesRequest {
    return { activeOnly: isSet(object.activeOnly) ? globalThis.Boolean(object.activeOnly) : false };
  },

  toJSON(message: ListPoliciesRequest): unknown {
    const obj: any = {};
    if (message.activeOnly === true) {
      obj.activeOnly = message.activeOnly;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ListPoliciesRequest>, I>>(base?: I): ListPoliciesRequest {
    return ListPoliciesRequest.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ListPoliciesRequest>, I>>(object: I): ListPoliciesRequest {
    const message = createBaseListPoliciesRequest();
    message.activeOnly = object.activeOnly ?? false;
    return message;
  },
};

function createBaseListPoliciesResponse(): ListPoliciesResponse {
  return { policies: [] };
}

export const ListPoliciesResponse = {
  encode(message: ListPoliciesResponse, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    for (const v of message.policies) {
      PolicyInfo.encode(v!, writer.uint32(10).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ListPoliciesResponse {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseListPoliciesResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.policies.push(PolicyInfo.decode(reader, reader.uint32()));
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ListPoliciesResponse {
    return {
      policies: globalThis.Array.isArray(object?.policies)
        ? object.policies.map((e: any) => PolicyInfo.fromJSON(e))
        : [],
    };
  },

  toJSON(message: ListPoliciesResponse): unknown {
    const obj: any = {};
    if (message.policies?.length) {
      obj.policies = message.policies.map((e) => PolicyInfo.toJSON(e));
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ListPoliciesResponse>, I>>(base?: I): ListPoliciesResponse {
    return ListPoliciesResponse.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ListPoliciesResponse>, I>>(object: I): ListPoliciesResponse {
    const message = createBaseListPoliciesResponse();
    message.policies = object.policies?.map((e) => PolicyInfo.fromPartial(e)) || [];
    return message;
  },
};

function createBaseApplyPolicyRequest(): ApplyPolicyRequest {
  return { policyName: "", dryRun: false, policyJson: "" };
}

export const ApplyPolicyRequest = {
  encode(message: ApplyPolicyRequest, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.policyName !== "") {
      writer.uint32(10).string(message.policyName);
    }
    if (message.dryRun === true) {
      writer.uint32(16).bool(message.dryRun);
    }
    if (message.policyJson !== "") {
      writer.uint32(26).string(message.policyJson);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ApplyPolicyRequest {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseApplyPolicyRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.policyName = reader.string();
          continue;
        case 2:
          if (tag !== 16) {
            break;
          }

          message.dryRun = reader.bool();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.policyJson = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ApplyPolicyRequest {
    return {
      policyName: isSet(object.policyName) ? globalThis.String(object.policyName) : "",
      dryRun: isSet(object.dryRun) ? globalThis.Boolean(object.dryRun) : false,
      policyJson: isSet(object.policyJson) ? globalThis.String(object.policyJson) : "",
    };
  },

  toJSON(message: ApplyPolicyRequest): unknown {
    const obj: any = {};
    if (message.policyName !== "") {
      obj.policyName = message.policyName;
    }
    if (message.dryRun === true) {
      obj.dryRun = message.dryRun;
    }
    if (message.policyJson !== "") {
      obj.policyJson = message.policyJson;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ApplyPolicyRequest>, I>>(base?: I): ApplyPolicyRequest {
    return ApplyPolicyRequest.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ApplyPolicyRequest>, I>>(object: I): ApplyPolicyRequest {
    const message = createBaseApplyPolicyRequest();
    message.policyName = object.policyName ?? "";
    message.dryRun = object.dryRun ?? false;
    message.policyJson = object.policyJson ?? "";
    return message;
  },
};

function createBaseApplyPolicyResponse(): ApplyPolicyResponse {
  return {
    success: false,
    message: "",
    groupsAdded: 0,
    groupsUpdated: 0,
    policiesAdded: 0,
    policiesUpdated: 0,
    policiesDeleted: 0,
    errors: [],
    tableAccessAdded: 0,
    tableAccessUpdated: 0,
    tableAccessDeleted: 0,
    drift: [],
  };
}

export const ApplyPolicyResponse = {
  encode(message: ApplyPolicyResponse, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.success === true) {
      writer.uint32(8).bool(message.success);
    }
    if (message.message !== "") {
      writer.uint32(18).string(message.message);
    }
    if (message.groupsAdded !== 0) {
      writer.uint32(24).int32(message.groupsAdded);
    }
    if (message.groupsUpdated !== 0) {
      writer.uint32(32).int32(message.groupsUpdated);
    }
    if (message.policiesAdded !== 0) {
      writer.uint32(40).int32(message.policiesAdded);
    }
    if (message.policiesUpdated !== 0) {
      writer.uint32(48).int32(message.policiesUpdated);
    }
    if (message.policiesDeleted !== 0) {
      writer.uint32(56).int32(message.policiesDeleted);
    }
    for (const v of message.errors) {
      writer.uint32(66).string(v!);
    }
    if (message.tableAccessAdded !== 0) {
      writer.uint32(72).int32(message.tableAccessAdded);
    }
    if (message.tableAccessUpdated !== 0) {
      writer.uint32(80).int32(message.tableAccessUpdated);
    }
    if (message.tableAccessDeleted !== 0) {
      writer.uint32(88).int32(message.tableAccessDeleted);
    }
    for (const v of message.drift) {
      writer.uint32(98).string(v!);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ApplyPolicyResponse {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseApplyPolicyResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.success = reader.bool();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.message = reader.string();
          continue;
        case 3:
          if (tag !== 24) {
            break;
          }

          message.groupsAdded = reader.int32();
          continue;
        case 4:
          if (tag !== 32) {
            break;
          }

          message.groupsUpdated = reader.int32();
          continue;
        case 5:
          if (tag !== 40) {
            break;
          }

          message.policiesAdded = reader.int32();
          continue;
        case 6:
          if (tag !== 48) {
            break;
          }

          message.policiesUpdated = reader.int32();
          continue;
        case 7:
          if (tag !== 56) {
            break;
          }

          message.policiesDeleted = reader.int32();
          continue;
        case 8:
          if (tag !== 66) {
            break;
          }

          message.errors.push(reader.string());
          continue;
        case 9:
          if (tag !== 72) {
            break;
          }

          message.tableAccessAdded = reader.int32();
          continue;
        case 10:
          if (tag !== 80) {
            break;
          }

          message.tableAccessUpdated = reader.int32();
          continue;
        case 11:
          if (tag !== 88) {
            break;
          }

          message.tableAccessDeleted = reader.int32();
          continue;
        case 12:
          if (tag !== 98) {
            break;
          }

          message.drift.push(reader.string());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ApplyPolicyResponse {
    return {
      success: isSet(object.success) ? globalThis.Boolean(object.success) : false,
      message: isSet(object.message) ? globalThis.String(object.message) : "",
      groupsAdded: isSet(object.groupsAdded) ? globalThis.Number(object.groupsAdded) : 0,
      groupsUpdated: isSet(object.groupsUpdated) ? globalThis.Number(object.groupsUpdated) : 0,
      policiesAdded: isSet(object.policiesAdded) ? globalThis.Number(object.policiesAdded) : 0,
      policiesUpdated: isSet(object.policiesUpdated) ? globalThis.Number(object.policiesUpdated) : 0,
      policiesDeleted: isSet(object.policiesDeleted) ? globalThis.Number(object.policiesDeleted) : 0,
      errors: globalThis.Array.isArray(object?.errors) ? object.errors.map((e: any) => globalThis.String(e)) : [],
      tableAccessAdded: isSet(object.tableAccessAdded) ? globalThis.Number(object.tableAccessAdded) : 0,
      tableAccessUpdated: isSet(object.tableAccessUpdated) ? globalThis.Number(object.tableAccessUpdated) : 0,
      tableAccessDeleted: isSet(object.tableAccessDeleted) ? globalThis.Number(object.tableAccessDeleted) : 0,
      drift: globalThis.Array.isArray(object?.drift) ? object.drift.map((e: any) => globalThis.String(e)) : [],
    };
  },

  toJSON(message: ApplyPolicyResponse): unknown {
    const obj: any = {};
    if (message.success === true) {
      obj.success = message.success;
    }
    if (message.message !== "") {
      obj.message = message.message;
    }
    if (message.groupsAdded !== 0) {
      obj.groupsAdded = Math.round(message.groupsAdded);
    }
    if (message.groupsUpdated !== 0) {
      obj.groupsUpdated = Math.round(message.groupsUpdated);
    }
    if (message.policiesAdded !== 0) {
      obj.policiesAdded = Math.round(message.policiesAdded);
    }
    if (message.policiesUpdated !== 0) {
      obj.policiesUpdated = Math.round(message.policiesUpdated);
    }
    if (message.policiesDeleted !== 0) {
      obj.policiesDeleted = Math.round(message.policiesDeleted);
    }
    if (message.errors?.length) {
      obj.errors = message.errors;
    }
    if (message.tableAccessAdded !== 0) {
      obj.tableAccessAdded = Math.round(message.tableAccessAdded);
    }
    if (message.tableAccessUpdated !== 0) {
      obj.tableAccessUpdated = Math.round(message.tableAccessUpdated);
    }
    if (message.tableAccessDeleted !== 0) {
      obj.tableAccessDeleted = Math.round(message.tableAccessDeleted);
    }
    if (message.drift?.length) {
      obj.drift = message.drift;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ApplyPolicyResponse>, I>>(base?: I): ApplyPolicyResponse {
    return ApplyPolicyResponse.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ApplyPolicyResponse>, I>>(object: I): ApplyPolicyResponse {
    const message = createBaseApplyPolicyResponse();
    message.success = object.success ?? false;
    message.message = object.message ?? "";
    message.groupsAdded = object.groupsAdded ?? 0;
    message.groupsUpdated = object.groupsUpdated ?? 0;
    message.policiesAdded = object.policiesAdded ?? 0;
    message.policiesUpdated = object.policiesUpdated ?? 0;
    message.policiesDeleted = object.policiesDeleted ?? 0;
    message.errors = object.errors?.map((e) => e) || [];
    message.tableAccessAdded = object.tableAccessAdded ?? 0;
    message.tableAccessUpdated = object.tableAccessUpdated ?? 0;
    message.tableAccessDeleted = object.tableAccessDeleted ?? 0;
    message.drift = object.drift?.map((e) => e) || [];
    return message;
  },
};

/** Auth service provides authentication and authorization */
export interface Auth {
  LoginBegin(request: LoginBeginRequest): Promise<LoginBeginResponse>;
  LoginComplete(request: LoginCompleteRequest): Promise<LoginCompleteResponse>;
  HasRole(request: HasRoleRequest): Promise<HasRoleResponse>;
  GetUserInfo(request: GetUserInfoRequest): Promise<GetUserInfoResponse>;
  GetUserInfoByMailbox(request: GetUserInfoByMailboxRequest): Promise<GetUserInfoByMailboxResponse>;
  GetUserInfoForSelf(request: GetUserInfoForSelfRequest): Promise<GetUserInfoForSelfResponse>;
  ManageRoleOverride(request: ManageRoleOverrideRequest): Promise<ManageRoleOverrideResponse>;
  RemoveRoleOverride(request: RemoveRoleOverrideRequest): Promise<RemoveRoleOverrideResponse>;
  ImpersonateUser(request: ImpersonateUserRequest): Promise<ImpersonateUserResponse>;
  /**
   * ListRoleOverrides and ListAllEndpointAuth: types exist in auth.pb.go
   * but were never written into this .proto. Drift to clean up separately;
   * commenting out here lets the rest of the file regenerate.
   * rpc ListRoleOverrides(ListRoleOverridesRequest) returns (ListRoleOverridesResponse);
   * rpc ListAllEndpointAuth(ListAllEndpointAuthRequest) returns (ListAllEndpointAuthResponse);
   */
  TestEndpointAccess(request: TestEndpointAccessRequest): Promise<TestEndpointAccessResponse>;
  UpsertPolicy(request: UpsertPolicyRequest): Promise<UpsertPolicyResponse>;
  ListPolicies(request: ListPoliciesRequest): Promise<ListPoliciesResponse>;
  ApplyPolicy(request: ApplyPolicyRequest): Promise<ApplyPolicyResponse>;
}

export const AuthServiceName = "auth.Auth";
export class AuthClientImpl implements Auth {
  private readonly rpc: Rpc;
  private readonly service: string;
  constructor(rpc: Rpc, opts?: { service?: string }) {
    this.service = opts?.service || AuthServiceName;
    this.rpc = rpc;
    this.LoginBegin = this.LoginBegin.bind(this);
    this.LoginComplete = this.LoginComplete.bind(this);
    this.HasRole = this.HasRole.bind(this);
    this.GetUserInfo = this.GetUserInfo.bind(this);
    this.GetUserInfoByMailbox = this.GetUserInfoByMailbox.bind(this);
    this.GetUserInfoForSelf = this.GetUserInfoForSelf.bind(this);
    this.ManageRoleOverride = this.ManageRoleOverride.bind(this);
    this.RemoveRoleOverride = this.RemoveRoleOverride.bind(this);
    this.ImpersonateUser = this.ImpersonateUser.bind(this);
    this.TestEndpointAccess = this.TestEndpointAccess.bind(this);
    this.UpsertPolicy = this.UpsertPolicy.bind(this);
    this.ListPolicies = this.ListPolicies.bind(this);
    this.ApplyPolicy = this.ApplyPolicy.bind(this);
  }
  LoginBegin(request: LoginBeginRequest): Promise<LoginBeginResponse> {
    const data = LoginBeginRequest.encode(request).finish();
    const promise = this.rpc.request(this.service, "LoginBegin", data);
    return promise.then((data) => LoginBeginResponse.decode(_m0.Reader.create(data)));
  }

  LoginComplete(request: LoginCompleteRequest): Promise<LoginCompleteResponse> {
    const data = LoginCompleteRequest.encode(request).finish();
    const promise = this.rpc.request(this.service, "LoginComplete", data);
    return promise.then((data) => LoginCompleteResponse.decode(_m0.Reader.create(data)));
  }

  HasRole(request: HasRoleRequest): Promise<HasRoleResponse> {
    const data = HasRoleRequest.encode(request).finish();
    const promise = this.rpc.request(this.service, "HasRole", data);
    return promise.then((data) => HasRoleResponse.decode(_m0.Reader.create(data)));
  }

  GetUserInfo(request: GetUserInfoRequest): Promise<GetUserInfoResponse> {
    const data = GetUserInfoRequest.encode(request).finish();
    const promise = this.rpc.request(this.service, "GetUserInfo", data);
    return promise.then((data) => GetUserInfoResponse.decode(_m0.Reader.create(data)));
  }

  GetUserInfoByMailbox(request: GetUserInfoByMailboxRequest): Promise<GetUserInfoByMailboxResponse> {
    const data = GetUserInfoByMailboxRequest.encode(request).finish();
    const promise = this.rpc.request(this.service, "GetUserInfoByMailbox", data);
    return promise.then((data) => GetUserInfoByMailboxResponse.decode(_m0.Reader.create(data)));
  }

  GetUserInfoForSelf(request: GetUserInfoForSelfRequest): Promise<GetUserInfoForSelfResponse> {
    const data = GetUserInfoForSelfRequest.encode(request).finish();
    const promise = this.rpc.request(this.service, "GetUserInfoForSelf", data);
    return promise.then((data) => GetUserInfoForSelfResponse.decode(_m0.Reader.create(data)));
  }

  ManageRoleOverride(request: ManageRoleOverrideRequest): Promise<ManageRoleOverrideResponse> {
    const data = ManageRoleOverrideRequest.encode(request).finish();
    const promise = this.rpc.request(this.service, "ManageRoleOverride", data);
    return promise.then((data) => ManageRoleOverrideResponse.decode(_m0.Reader.create(data)));
  }

  RemoveRoleOverride(request: RemoveRoleOverrideRequest): Promise<RemoveRoleOverrideResponse> {
    const data = RemoveRoleOverrideRequest.encode(request).finish();
    const promise = this.rpc.request(this.service, "RemoveRoleOverride", data);
    return promise.then((data) => RemoveRoleOverrideResponse.decode(_m0.Reader.create(data)));
  }

  ImpersonateUser(request: ImpersonateUserRequest): Promise<ImpersonateUserResponse> {
    const data = ImpersonateUserRequest.encode(request).finish();
    const promise = this.rpc.request(this.service, "ImpersonateUser", data);
    return promise.then((data) => ImpersonateUserResponse.decode(_m0.Reader.create(data)));
  }

  TestEndpointAccess(request: TestEndpointAccessRequest): Promise<TestEndpointAccessResponse> {
    const data = TestEndpointAccessRequest.encode(request).finish();
    const promise = this.rpc.request(this.service, "TestEndpointAccess", data);
    return promise.then((data) => TestEndpointAccessResponse.decode(_m0.Reader.create(data)));
  }

  UpsertPolicy(request: UpsertPolicyRequest): Promise<UpsertPolicyResponse> {
    const data = UpsertPolicyRequest.encode(request).finish();
    const promise = this.rpc.request(this.service, "UpsertPolicy", data);
    return promise.then((data) => UpsertPolicyResponse.decode(_m0.Reader.create(data)));
  }

  ListPolicies(request: ListPoliciesRequest): Promise<ListPoliciesResponse> {
    const data = ListPoliciesRequest.encode(request).finish();
    const promise = this.rpc.request(this.service, "ListPolicies", data);
    return promise.then((data) => ListPoliciesResponse.decode(_m0.Reader.create(data)));
  }

  ApplyPolicy(request: ApplyPolicyRequest): Promise<ApplyPolicyResponse> {
    const data = ApplyPolicyRequest.encode(request).finish();
    const promise = this.rpc.request(this.service, "ApplyPolicy", data);
    return promise.then((data) => ApplyPolicyResponse.decode(_m0.Reader.create(data)));
  }
}

interface Rpc {
  request(service: string, method: string, data: Uint8Array): Promise<Uint8Array>;
}

function bytesFromBase64(b64: string): Uint8Array {
  if (globalThis.Buffer) {
    return Uint8Array.from(globalThis.Buffer.from(b64, "base64"));
  } else {
    const bin = globalThis.atob(b64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; ++i) {
      arr[i] = bin.charCodeAt(i);
    }
    return arr;
  }
}

function base64FromBytes(arr: Uint8Array): string {
  if (globalThis.Buffer) {
    return globalThis.Buffer.from(arr).toString("base64");
  } else {
    const bin: string[] = [];
    arr.forEach((byte) => {
      bin.push(globalThis.String.fromCharCode(byte));
    });
    return globalThis.btoa(bin.join(""));
  }
}

type Builtin = Date | Function | Uint8Array | string | number | boolean | undefined;

export type DeepPartial<T> = T extends Builtin ? T
  : T extends globalThis.Array<infer U> ? globalThis.Array<DeepPartial<U>>
  : T extends ReadonlyArray<infer U> ? ReadonlyArray<DeepPartial<U>>
  : T extends {} ? { [K in keyof T]?: DeepPartial<T[K]> }
  : Partial<T>;

type KeysOfUnion<T> = T extends T ? keyof T : never;
export type Exact<P, I extends P> = P extends Builtin ? P
  : P & { [K in keyof P]: Exact<P[K], I[K]> } & { [K in Exclude<keyof I, KeysOfUnion<P>>]: never };

function toTimestamp(date: Date): Timestamp {
  const seconds = Math.trunc(date.getTime() / 1_000).toString();
  const nanos = (date.getTime() % 1_000) * 1_000_000;
  return { seconds, nanos };
}

function fromTimestamp(t: Timestamp): Date {
  let millis = (globalThis.Number(t.seconds) || 0) * 1_000;
  millis += (t.nanos || 0) / 1_000_000;
  return new globalThis.Date(millis);
}

function fromJsonTimestamp(o: any): Date {
  if (o instanceof globalThis.Date) {
    return o;
  } else if (typeof o === "string") {
    return new globalThis.Date(o);
  } else {
    return fromTimestamp(Timestamp.fromJSON(o));
  }
}

function isObject(value: any): boolean {
  return typeof value === "object" && value !== null;
}

function isSet(value: any): boolean {
  return value !== null && value !== undefined;
}
